<script setup>
import { computed, inject, nextTick, onMounted, ref, watch } from 'vue'
import dayjs from 'dayjs'
import ProductService from '@/services/ProductService'
import SearchableInput from './SearchableInput.vue'
import { useReferenceStore } from '@/stores/ReferenceStore'
import { ChevronDownIcon, PrinterIcon, XMarkIcon } from '@heroicons/vue/24/solid'
import apiClient from '@/apiClient'
import { referenceWorkspaceKey } from './reference/referenceWorkspace'
import {
  buildTimelineBarStyle,
  buildTimelineHours,
  buildTimelineMarkers,
  getTimelineEndMinutes,
  parseTimeToMinutes,
} from '@/utils/resourceTimeline'

const emit = defineEmits(['schedule-dirty'])

const props = defineProps({
  editable: { type: Boolean, required: false },
  reference: { type: Object, required: true },
  embedded: { type: Boolean, default: false },
  mode: {
    type: String,
    default: 'resources',
    validator: value => ['resources', 'schedule'].includes(value),
  },
})

const isScheduleMode = computed(() => props.mode === 'schedule')
const isResourceMode = computed(() => props.mode === 'resources')

const referenceStore = useReferenceStore()
const workspace = inject(referenceWorkspaceKey, null)
const showToast = workspace?.showToast ?? (() => {})
// still exposing the whole object so template can do `entries.dates`
const entries = computed({
  get: () => referenceStore.resources,
  set: (val) => referenceStore.setActiveVersionEntries(val)
})
const referenceData = computed(() => referenceStore.data)

const products = ref([])
const vehicles = ref([])
const vehiclesLoading = ref(false)
const vehiclesLoadError = ref('')
const vehicleAllocations = ref([])
const vehicleActionState = ref({})
const vehicleActionErrors = ref({})
const pendingVehicleSelections = ref({})
const personalVehicleDrafts = ref({})
const staffActionState = ref({})
const staffActionErrors = ref({})
const leadSaving = ref(false)

const assignedStaffOptions = computed(() => {
  const staff = new Map()
  ;(entries.value?.dates ?? []).forEach((date) => {
    ;(date?.resources ?? []).forEach((resource) => {
      ;(resource?.personnel_assignments ?? []).forEach((assignment) => {
        const member = assignment?.staff
        if (assignment?.id && member?.id) staff.set(Number(member.id), member)
      })
    })
  })

  return Array.from(staff.values()).sort((a, b) =>
    (a?.contact?.name ?? '').localeCompare(b?.contact?.name ?? '')
  )
})

const updateEventLead = async (value) => {
  if (leadSaving.value) return
  leadSaving.value = true
  try {
    await referenceStore.updateLead(value ? Number(value) : null)
  } catch (error) {
    console.error(error)
    alert(getApiErrorMessage(error, 'Unable to update the event lead.'))
  } finally {
    leadSaving.value = false
  }
}

onMounted(() => {
  loadProducts()
  if (isResourceMode.value) loadVehicles()
})

watch(
  () => referenceData.value?.vehicle_allocations,
  (next) => {
    vehicleAllocations.value = Array.isArray(next)
      ? next.map(item => ({ ...item }))
      : []
  },
  { immediate: true, deep: true }
)

async function loadProducts() {
  const svc = new ProductService()
  products.value = await svc.index()
  // pick first as default
  dateTime.value.product = products.value.length ? products.value[0].id : null
}

async function loadVehicles() {
  if (vehiclesLoading.value) return
  vehiclesLoading.value = true
  vehiclesLoadError.value = ''

  try {
    const { data } = await apiClient.get('/api/vehicle?active=true&per_page=250', {
      suppressAutoReload: true,
      suppressErrorAlert: true,
    })
    const list = Array.isArray(data?.data) ? data.data : []
    vehicles.value = list.sort((a, b) => {
      const aRegistration = a?.registrationNumber ?? ''
      const bRegistration = b?.registrationNumber ?? ''
      return aRegistration.localeCompare(bRegistration)
    })
  } catch (error) {
    console.error(error)
    vehicles.value = []
    vehiclesLoadError.value = getApiErrorMessage(
      error,
      'Unable to load active vehicles. Try again.',
    )
  } finally {
    vehiclesLoading.value = false
  }
}

const getApiErrorMessage = (error, fallback = 'Unable to save changes.') => {
  if (error?.response?.data?.message) {
    return error.response.data.message
  }

  const validation = error?.response?.data?.errors
  if (validation && typeof validation === 'object') {
    const first = Object.values(validation).flat().find(Boolean)
    if (first) {
      return first
    }
  }

  return fallback
}

const getVehicleAllocations = () =>
  Array.isArray(vehicleAllocations.value) ? vehicleAllocations.value : []

const setVehicleAllocations = (next) => {
  const allocations = Array.isArray(next) ? next : []
  vehicleAllocations.value = allocations

  if (referenceData.value && typeof referenceData.value === 'object') {
    referenceData.value.vehicle_allocations = allocations.map(item => ({ ...item }))
  }
}

const setVehicleBusy = (key, isBusy) => {
  vehicleActionState.value = {
    ...vehicleActionState.value,
    [key]: isBusy,
  }
}

const isVehicleBusy = (key) => Boolean(vehicleActionState.value[key])

const setVehicleActionError = (jobDateId, message = '') => {
  vehicleActionErrors.value = {
    ...vehicleActionErrors.value,
    [jobDateId]: message,
  }
}

const getAddVehiclePlaceholder = (dateEntry) => {
  if (vehiclesLoading.value) return 'Loading active vehicles…'
  if (vehiclesLoadError.value) return 'Vehicles unavailable — retry above'
  if (vehicles.value.length === 0) return 'No active vehicles available'
  if (getAddableVehicles(dateEntry).length === 0) return 'All active vehicles assigned'
  return '+ Add another vehicle'
}

const getVehicleLabel = (vehicle) => {
  if (!vehicle) {
    return 'Unknown vehicle'
  }
  const registration = vehicle.registrationNumber ?? `Vehicle #${vehicle.id}`
  const details = [vehicle.make, vehicle.model].filter(Boolean).join(' ')
  return details ? `${registration} · ${details}` : registration
}

const isPersonalVehicleAllocation = (allocation) =>
  allocation?.vehicle_source === 'personal'

const getAllocationVehicleLabel = (allocation) => {
  if (isPersonalVehicleAllocation(allocation)) {
    return `${allocation.registration ?? allocation.personal_vehicle_registration ?? 'Registration required'} · Own car`
  }

  return getVehicleLabel(allocation?.vehicle)
}

const vehicleAllocationKey = (allocation) => isPersonalVehicleAllocation(allocation)
  ? `personal:${String(allocation.registration ?? allocation.personal_vehicle_registration ?? '').replace(/[^a-z0-9]/gi, '').toUpperCase()}`
  : `fleet:${Number(allocation?.vehicle_id)}`

const getAllocationContextEntries = (context) =>
  Array.isArray(context?.entries) ? context.entries : [context].filter(Boolean)

const getVehicleActionError = (context) => {
  const dateIds = getAllocationContextEntries(context)
    .map(entry => entry?.id ?? entry?.date_id)
  return dateIds.map(id => vehicleActionErrors.value[id]).find(Boolean) ?? ''
}

const getAllocationContextDateIds = (context) => new Set(
  getAllocationContextEntries(context)
    .map(entry => Number(entry?.id ?? entry?.date_id))
    .filter(Boolean)
)

const getVehicleAllocationsForContext = (context) => {
  const jobDateIds = getAllocationContextDateIds(context)
  return getVehicleAllocations().filter(allocation =>
    jobDateIds.has(Number(allocation?.job_date_id))
  )
}

const quotedVehicleMaximum = computed(() => {
  const maximum = referenceData.value?.vehicle_allocation_limit?.quoted_max
  return maximum === null || maximum === undefined || maximum === ''
    ? null
    : Math.max(0, Number(maximum))
})

const getVehicleLimitMessage = (context) => {
  if (quotedVehicleMaximum.value === null) return ''
  const allocated = getVehicleAllocationsForContext(context).length
  if (allocated <= quotedVehicleMaximum.value) return ''
  const noun = quotedVehicleMaximum.value === 1 ? 'vehicle' : 'vehicles'
  return `The accepted quote allows ${quotedVehicleMaximum.value} ${noun}; ${allocated} are allocated to this shift. Super Users have been notified.`
}

const getVehicleAllocationContextDate = (context) =>
  getAllocationContextEntries(context).find(entry => entry?.id ?? entry?.date_id) ?? null

const isVehicleAllocatedElsewhere = (context, allocation, vehicleId) =>
  getVehicleAllocationsForContext(context).some(
    entry => Number(entry.id) !== Number(allocation.id)
      && Number(entry.vehicle_id) === Number(vehicleId)
  )

const getAddableVehicles = (context) => {
  const allocated = new Set(
    getVehicleAllocationsForContext(context)
      .map(entry => Number(entry.vehicle_id))
      .filter(Boolean)
  )
  return vehicles.value.filter(vehicle => !allocated.has(Number(vehicle.id)))
}

const hasDriverSkill = (staff) =>
  (staff?.skills ?? []).some(skill =>
    (skill?.name ?? '').toLowerCase().includes('driver')
  )

const getAssignedDriverOptions = (context) => {
  const candidates = new Map()

  getAllocationContextEntries(context).forEach((dateEntry) => {
    ;(dateEntry?.resources ?? []).forEach((resource) => {
      ;(resource.personnel_assignments ?? []).forEach((assignment) => {
        const staff = assignment?.staff
        if (assignment?.id && staff?.id && hasDriverSkill(staff) && !candidates.has(staff.id)) {
          candidates.set(staff.id, staff)
        }
      })
    })
  })

  return Array.from(candidates.values()).sort((a, b) =>
    (a?.contact?.name ?? '').localeCompare(b?.contact?.name ?? '')
  )
}

const getDriverOptionsForAllocation = (context, allocation) => {
  const options = getAssignedDriverOptions(context)
  if (allocation?.driver?.id && !options.find(staff => staff.id === allocation.driver.id)) {
    options.push(allocation.driver)
  }
  return options.sort((a, b) =>
    (a?.contact?.name ?? '').localeCompare(b?.contact?.name ?? '')
  )
}

const isDriverAllocatedElsewhere = (context, allocation, driverId) =>
  getVehicleAllocationsForContext(context).some(
    entry => Number(entry.id) !== Number(allocation?.id)
      && Number(entry.driver_staff_id) === Number(driverId)
  )

const mergeVehicleAllocation = (incomingAllocation) => {
  const allocations = getVehicleAllocations()
  const index = allocations.findIndex(entry => Number(entry.id) === Number(incomingAllocation.id))
  if (index >= 0) {
    allocations[index] = incomingAllocation
  } else {
    allocations.push(incomingAllocation)
  }
  setVehicleAllocations([...allocations])
}

const createVehicleAllocation = async (dateEntry, vehicleId, driverStaffId = null) => {
  const { data } = await apiClient.post(
    '/api/vehicle-allocation',
    {
      reference_id: referenceData.value.id,
      job_date_id: Number(dateEntry.id),
      vehicle_id: Number(vehicleId),
      driver_staff_id: driverStaffId ? Number(driverStaffId) : null,
    },
    { suppressAutoReload: true, suppressErrorAlert: true },
  )
  mergeVehicleAllocation(data)
  return data
}

const addVehicleAllocation = async (dateEntry, vehicleId, context = dateEntry) => {
  if (!referenceData.value?.id || !dateEntry?.id || !vehicleId) {
    return
  }

  const busyKey = `create-${dateEntry.id}`
  if (isVehicleBusy(busyKey)) {
    return
  }
  setVehicleBusy(busyKey, true)
  setVehicleActionError(dateEntry.id)

  try {
    await createVehicleAllocation(dateEntry, vehicleId)
    showToast('Vehicle allocated.')
  } catch (error) {
    console.error(error)
    setVehicleActionError(
      dateEntry.id,
      getApiErrorMessage(error, 'Unable to allocate vehicle.'),
    )
  } finally {
    pendingVehicleSelections.value = {
      ...pendingVehicleSelections.value,
      [dateEntry.id]: '',
    }
    setVehicleBusy(busyKey, false)
  }
}

const updateVehicleAllocation = async (allocation, payload) => {
  if (!allocation?.id) {
    return
  }

  const busyKey = `allocation-${allocation.id}`
  if (isVehicleBusy(busyKey)) {
    return
  }
  setVehicleBusy(busyKey, true)
  setVehicleActionError(allocation.job_date_id)

  try {
    const { data } = await apiClient.put(
      `/api/vehicle-allocation/${allocation.id}`,
      payload,
      { suppressAutoReload: true, suppressErrorAlert: true },
    )
    mergeVehicleAllocation(data)
    showToast('Vehicle allocation updated.')
  } catch (error) {
    console.error(error)
    setVehicleActionError(
      allocation.job_date_id,
      getApiErrorMessage(error, 'Unable to update vehicle allocation.'),
    )
  } finally {
    setVehicleBusy(busyKey, false)
  }
}

const removeVehicleAllocation = async (allocation) => {
  if (!allocation?.id) {
    return
  }

  const busyKey = `allocation-${allocation.id}`
  if (isVehicleBusy(busyKey)) {
    return
  }
  setVehicleBusy(busyKey, true)
  setVehicleActionError(allocation.job_date_id)

  try {
    await apiClient.delete(`/api/vehicle-allocation/${allocation.id}`, {
      suppressAutoReload: true,
      suppressErrorAlert: true,
    })
    setVehicleAllocations(
      getVehicleAllocations().filter(entry => Number(entry.id) !== Number(allocation.id))
    )
    showToast('Vehicle allocation removed.')
  } catch (error) {
    console.error(error)
    setVehicleActionError(
      allocation.job_date_id,
      getApiErrorMessage(error, 'Unable to remove vehicle allocation.'),
    )
  } finally {
    setVehicleBusy(busyKey, false)
  }
}

const onAllocationVehicleChanged = async (allocation, vehicleId) => {
  const parsedVehicleId = Number(vehicleId)
  if (!parsedVehicleId || parsedVehicleId === Number(allocation?.vehicle_id)) {
    return
  }

  await updateVehicleAllocation(allocation, {
    job_date_id: Number(allocation?.job_date_id),
    vehicle_id: parsedVehicleId,
    driver_staff_id: allocation?.driver_staff_id ?? null,
  })
}

const onAllocationDriverChanged = async (allocation, driverStaffId) => {
  const parsedDriverId = driverStaffId ? Number(driverStaffId) : null
  if (
    (parsedDriverId === null && !allocation?.driver_staff_id) ||
    parsedDriverId === Number(allocation?.driver_staff_id)
  ) {
    return
  }

  await updateVehicleAllocation(allocation, {
    job_date_id: Number(allocation?.job_date_id),
    vehicle_source: allocation?.vehicle_source ?? 'fleet',
    vehicle_id: allocation?.vehicle_id ? Number(allocation.vehicle_id) : null,
    personal_vehicle_registration: allocation?.personal_vehicle_registration ?? null,
    driver_staff_id: parsedDriverId,
  })
}

const onPersonalVehicleRegistrationChanged = async (allocation, registration) => {
  const value = String(registration ?? '').trim()
  if (!value || value === allocation?.personal_vehicle_registration) return

  await updateVehicleAllocation(allocation, {
    job_date_id: Number(allocation?.job_date_id),
    vehicle_source: 'personal',
    vehicle_id: null,
    personal_vehicle_registration: value,
    driver_staff_id: allocation?.driver_staff_id ?? null,
  })
}

const personalVehicleDraftFor = (dateEntry) =>
  personalVehicleDrafts.value[dateEntry?.id] ?? null

const startPersonalVehicle = (dateEntry, context = dateEntry) => {
  personalVehicleDrafts.value = {
    ...personalVehicleDrafts.value,
    [dateEntry.id]: { registration: '', driver_staff_id: '' },
  }
}

const cancelPersonalVehicle = (dateEntry) => {
  const next = { ...personalVehicleDrafts.value }
  delete next[dateEntry.id]
  personalVehicleDrafts.value = next
}

const updatePersonalVehicleDraft = (dateEntry, field, value) => {
  personalVehicleDrafts.value = {
    ...personalVehicleDrafts.value,
    [dateEntry.id]: {
      ...personalVehicleDraftFor(dateEntry),
      [field]: value,
    },
  }
}

const addPersonalVehicle = async (dateEntry, context = dateEntry) => {
  const draft = personalVehicleDraftFor(dateEntry)
  if (!referenceData.value?.id || !dateEntry?.id || !draft?.registration || !draft?.driver_staff_id) {
    return
  }

  const busyKey = `create-personal-${dateEntry.id}`
  if (isVehicleBusy(busyKey)) return
  setVehicleBusy(busyKey, true)
  setVehicleActionError(dateEntry.id)

  try {
    const { data } = await apiClient.post(
      '/api/vehicle-allocation',
      {
        reference_id: referenceData.value.id,
        job_date_id: Number(dateEntry.id),
        vehicle_source: 'personal',
        vehicle_id: null,
        personal_vehicle_registration: draft.registration,
        driver_staff_id: Number(draft.driver_staff_id),
      },
      { suppressAutoReload: true, suppressErrorAlert: true },
    )
    mergeVehicleAllocation(data)
    cancelPersonalVehicle(dateEntry)
    showToast("Staff vehicle allocated.")
  } catch (error) {
    console.error(error)
    setVehicleActionError(
      dateEntry.id,
      getApiErrorMessage(error, 'Unable to allocate the staff vehicle.'),
    )
  } finally {
    setVehicleBusy(busyKey, false)
  }
}

const onAddVehicleSelectionChanged = async (dateEntry, vehicleId, context = dateEntry) => {
  pendingVehicleSelections.value = {
    ...pendingVehicleSelections.value,
    [dateEntry.id]: vehicleId,
  }
  if (!vehicleId) {
    return
  }

  await addVehicleAllocation(dateEntry, vehicleId, context)
}

const getPendingVehicleSelection = (dateEntry) =>
  pendingVehicleSelections.value[dateEntry?.id] ?? ''

const getVehicleAllocationDateForSlotGroup = (slotGroup) => {
  const savedEntries = getSavedEntriesForSlotGroup(slotGroup)
  if (!savedEntries.length) return null

  const allocatedDateIds = new Set(
    getVehicleAllocationsForSlotGroup(slotGroup)
      .map(allocation => Number(allocation?.job_date_id))
  )

  return savedEntries.find(entry => allocatedDateIds.has(Number(entry?.id ?? entry?.date_id)))
    ?? savedEntries[0]
}

const getVehicleAllocationDatesForSlotGroup = (slotGroup) => {
  const allocationDate = getVehicleAllocationDateForSlotGroup(slotGroup)
  return allocationDate ? [allocationDate] : []
}

const getPreviousAllocationSlotGroup = (slotGroup) => {
  const savedSlotGroups = slotGroups.value.filter(
    candidate => getSavedEntriesForSlotGroup(candidate).length > 0
  )
  const index = savedSlotGroups.findIndex(candidate => candidate.renderKey === slotGroup?.renderKey)
  return index > 0 ? savedSlotGroups[index - 1] : null
}

const canCopyPreviousAssignments = (slotGroup) => {
  const previous = getPreviousAllocationSlotGroup(slotGroup)
  if (!previous) return false

  const existingVehicleKeys = new Set(
    getVehicleAllocationsForSlotGroup(slotGroup).map(vehicleAllocationKey)
  )
  const eligibleDriverIds = new Set(
    getAssignedDriverOptions(slotGroup).map(driver => Number(driver.id))
  )
  const usedDriverIds = new Set(
    getVehicleAllocationsForSlotGroup(slotGroup)
      .map(allocation => Number(allocation.driver_staff_id))
      .filter(Boolean)
  )
  return getVehicleAllocationsForSlotGroup(previous).some(
    allocation => !existingVehicleKeys.has(vehicleAllocationKey(allocation))
      && (!isPersonalVehicleAllocation(allocation)
        || (eligibleDriverIds.has(Number(allocation.driver_staff_id))
          && !usedDriverIds.has(Number(allocation.driver_staff_id))))
  )
}

const copyPreviousAssignments = async (slotGroup) => {
  const previous = getPreviousAllocationSlotGroup(slotGroup)
  const dateEntry = getVehicleAllocationDateForSlotGroup(slotGroup)
  if (!previous || !dateEntry || !canCopyPreviousAssignments(slotGroup)) return

  const busyKey = `copy-${dateEntry.id}`
  if (isVehicleBusy(busyKey)) return
  setVehicleBusy(busyKey, true)
  setVehicleActionError(dateEntry.id)

  const existingVehicleKeys = new Set(
    getVehicleAllocationsForSlotGroup(slotGroup).map(vehicleAllocationKey)
  )
  const eligibleDriverIds = new Set(
    getAssignedDriverOptions(slotGroup).map(driver => Number(driver.id))
  )
  const usedDriverIds = new Set(
    getVehicleAllocationsForSlotGroup(slotGroup)
      .map(allocation => Number(allocation.driver_staff_id))
      .filter(Boolean)
  )

  try {
    for (const allocation of getVehicleAllocationsForSlotGroup(previous)) {
      if (existingVehicleKeys.has(vehicleAllocationKey(allocation))) continue
      const driverId = eligibleDriverIds.has(Number(allocation.driver_staff_id))
        && !usedDriverIds.has(Number(allocation.driver_staff_id))
        ? allocation.driver_staff_id
        : null
      if (isPersonalVehicleAllocation(allocation)) {
        if (!driverId) continue
        const { data } = await apiClient.post(
          '/api/vehicle-allocation',
          {
            reference_id: referenceData.value.id,
            job_date_id: Number(dateEntry.id),
            vehicle_source: 'personal',
            vehicle_id: null,
            personal_vehicle_registration: allocation.personal_vehicle_registration ?? allocation.registration,
            driver_staff_id: Number(driverId),
          },
          { suppressAutoReload: true, suppressErrorAlert: true },
        )
        mergeVehicleAllocation(data)
      } else {
        await createVehicleAllocation(dateEntry, allocation.vehicle_id, driverId)
      }
      if (driverId) usedDriverIds.add(Number(driverId))
    }
    showToast('Previous shift vehicle assignments copied.')
  } catch (error) {
    console.error(error)
    setVehicleActionError(
      dateEntry.id,
      getApiErrorMessage(error, 'Unable to copy the previous shift assignments.'),
    )
  } finally {
    setVehicleBusy(busyKey, false)
  }
}

const formatDurationLabel = (totalMinutes) => {
  const minutesValue = Math.max(0, Number(totalMinutes) || 0)
  const hours = Math.floor(minutesValue / 60)
  const minutes = minutesValue % 60

  if (!minutes) {
    return `${hours} hr${hours === 1 ? '' : 's'}`
  }

  if (!hours) {
    return `${minutes} min${minutes === 1 ? '' : 's'}`
  }

  return `${hours} hr${hours === 1 ? '' : 's'} ${minutes} min${minutes === 1 ? '' : 's'}`
}

const getDuration = (startTime, endTime) => {
  const startMinutes = parseTimeToMinutes(startTime)
  let finishMinutes = parseTimeToMinutes(endTime)

  if (finishMinutes < startMinutes) {
    finishMinutes += 24 * 60
  }

  return formatDurationLabel(finishMinutes - startMinutes)
}

const getProduct = (product_id) => {
  return products.value.find(p => p.id === product_id)
}

const getProductName = (product_id) => {
  return getProduct(product_id)?.name ?? "Unknown Unit"
}

const getAddressText = (value) => {
  if (value === null || value === undefined) {
    return ''
  }

  if (typeof value === 'string' || typeof value === 'number') {
    return `${value}`
  }

  if (typeof value !== 'object') {
    return ''
  }

  return getAddressText(
    value.data
      ?? value.location_address
      ?? value.address?.data
      ?? value.label
      ?? ''
  )
}

const normalizeAddressText = (value) => getAddressText(value)
  .trim()
  .replace(/\s+/g, ' ')

const normalizeAddressKey = (value) => normalizeAddressText(value)
  .toLowerCase()

const addressOptions = computed(() => {
  const options = []
  const seen = new Set()
  const numericId = (value) => (/^\d+$/.test(String(value ?? '')) ? Number(value) : null)
  const addAddress = (address, index = 0, prefix = 'reference-address') => {
    const normalizedAddress = normalizeAddressText(address?.data ?? address)
    if (!normalizedAddress) return

    const key = normalizeAddressKey(normalizedAddress)
    if (!key || seen.has(key)) return

    const addressId = address?.address_id ?? numericId(address?.id)

    seen.add(key)
    options.push({
      id: address?.id ?? `${prefix}-${index}`,
      data: normalizedAddress,
      address_id: addressId,
      latitude: address?.latitude ?? null,
      longitude: address?.longitude ?? null,
    })
  }

  ;(referenceData.value?.versions?.[referenceStore.activeVersion]?.locations ?? []).forEach((location, index) => {
    addAddress({
      id: location?.id ?? `reference-location-${index}`,
      data: location?.location_address ?? location?.address?.data ?? null,
      address_id: location?.address_id ?? location?.address?.id ?? null,
      latitude: location?.address?.latitude ?? null,
      longitude: location?.address?.longitude ?? null,
    }, index, 'reference-location')
  })

  ;(entries.value?.dates ?? []).forEach((entry, index) => {
    addAddress({
      id: `reference-address-${index}`,
      data: entry?.location_address ?? entry?.address?.data ?? null,
      address_id: entry?.address_id ?? entry?.address?.id ?? null,
      latitude: entry?.address?.latitude ?? null,
      longitude: entry?.address?.longitude ?? null,
    }, index)
  })

  return options
})

const hasAddresses = computed(() => addressOptions.value.length > 0)

const isCustomAddress = (slotGroup) => {
  const address = normalizeAddressText(slotGroup?.location_address)
  if (!address) return false

  return !addressOptions.value.some((option) => option.data === address)
}

const compareTimeValues = (left, right) => parseTimeToMinutes(left) - parseTimeToMinutes(right)
const normalizeIsoDate = (value) => {
  const stringValue = (value ?? '').toString().trim()
  if (!stringValue) {
    return ''
  }

  const directMatch = stringValue.match(/^(\d{4}-\d{2}-\d{2})(?:$|T)/)
  if (directMatch) {
    return directMatch[1]
  }

  const parsed = new Date(stringValue)

  if (Number.isNaN(parsed.getTime())) {
    return ''
  }

  const year = parsed.getFullYear()
  const month = String(parsed.getMonth() + 1).padStart(2, '0')
  const day = String(parsed.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const getSlotGroupMatchKey = (entryOrGroup) =>
  [
    entryOrGroup?.date ?? 'undated',
    entryOrGroup?.startTime ?? '00:00:00',
    entryOrGroup?.finishTime ?? '00:00:00',
    getAddressText(entryOrGroup?.location_name ?? ''),
    getAddressText(entryOrGroup?.location_address ?? ''),
  ].join('__')

const entryIdentities = new WeakMap()
let nextDraftEntryIdentity = 0
const getEntryIdentity = (entry) => {
  if (!entryIdentities.has(entry)) {
    entryIdentities.set(entry, entry.id ?? entry.date_id ?? `draft-entry-${nextDraftEntryIdentity++}`)
  }
  return entryIdentities.get(entry)
}

const getSlotGroupRenderKey = (entriesForGroup = []) =>
  entriesForGroup
    .map((entry, index) => getEntryIdentity(entry, index))
    .join('__')

const getEditorDayRenderKey = (slotGroupsForDay = [], dayIndex = 0) => {
  const groupKey = slotGroupsForDay
    .map((slotGroup, slotGroupIndex) => slotGroup?.renderKey ?? `draft-slot-group-${dayIndex}-${slotGroupIndex}`)
    .join('__')

  return groupKey || `draft-day-${dayIndex}`
}

const orderedEntries = computed(() =>
  [...(entries.value?.dates ?? [])].sort((left, right) => {
    const dateComparison = String(left?.date ?? '').localeCompare(String(right?.date ?? ''))
    if (dateComparison !== 0) {
      return dateComparison
    }

    const startComparison = compareTimeValues(left?.startTime, right?.startTime)
    if (startComparison !== 0) {
      return startComparison
    }

    return compareTimeValues(left?.finishTime, right?.finishTime)
  })
)

const getSavedEntriesForSlotGroup = (slotGroup) =>
  (slotGroup?.entries ?? []).filter(entry => Number(entry?.id ?? entry?.date_id) > 0)

const getVehicleAllocationsForSlotGroup = (slotGroup) =>
  getVehicleAllocationsForContext(slotGroup)

const getVehicleCountForDayGroup = (group) =>
  (group?.slotGroups ?? []).reduce(
    (total, slotGroup) => total + getVehicleAllocationsForSlotGroup(slotGroup).length,
    0,
  )

const slotGroups = computed(() =>
  orderedEntries.value.reduce((groups, entry, entryIndex) => {
    const matchKey = getSlotGroupMatchKey(entry)
    const existingGroup = groups.find(group => group.matchKey === matchKey)
    if (existingGroup) {
      existingGroup.entries.push(entry)
      existingGroup.resources.push(...(entry?.resources ?? []))
      existingGroup.renderKey = getSlotGroupRenderKey(existingGroup.entries)
      return groups
    }

    groups.push({
      matchKey,
      renderKey: getSlotGroupRenderKey([entry], entryIndex),
      date: entry?.date,
      startTime: entry?.startTime,
      finishTime: entry?.finishTime,
      location_name: entry?.location_name ?? null,
      location_address: normalizeAddressText(entry?.location_address ?? entry?.address?.data) || null,
      address_id: entry?.address_id ?? entry?.address?.id ?? null,
      address: entry?.address ?? null,
      entries: [entry],
      resources: [...(entry?.resources ?? [])],
    })
    return groups
  }, [])
)

const editorDayGroups = computed(() =>
  slotGroups.value.reduce((groups, slotGroup, slotGroupIndex) => {
    const matchKey = slotGroup?.date ?? 'undated'
    const existingGroup = groups.find(group => group.matchKey === matchKey)
    if (existingGroup) {
      existingGroup.slotGroups.push(slotGroup)
      existingGroup.resourceCount += slotGroup?.resources?.length ?? 0
      existingGroup.renderKey = getEditorDayRenderKey(existingGroup.slotGroups, slotGroupIndex)
      return groups
    }

    groups.push({
      matchKey,
      renderKey: getEditorDayRenderKey([slotGroup], slotGroupIndex),
      date: slotGroup?.date,
      slotGroups: [slotGroup],
      resourceCount: slotGroup?.resources?.length ?? 0,
    })
    return groups
  }, [])
)

const activeEditorDayKey = ref(null)
const editorDayRefs = ref({})
const dayDateDrafts = ref({})
const resourceSearchQuery = ref('')
const showUnfilledOnly = ref(false)
const expandedResourceGroups = ref({})

watch(
  editorDayGroups,
  (groups) => {
    if (!groups.length) {
      activeEditorDayKey.value = null
      return
    }

    if (!groups.some(group => group.renderKey === activeEditorDayKey.value)) {
      activeEditorDayKey.value = groups[0].renderKey
    }
  },
  { immediate: true },
)

const activeEditorDay = computed(() =>
  editorDayGroups.value.find(group => group.renderKey === activeEditorDayKey.value)
    ?? editorDayGroups.value[0]
    ?? null
)

const setEditorDayRef = (key, element) => {
  if (!key) return
  if (element) {
    editorDayRefs.value[key] = element
    return
  }
  delete editorDayRefs.value[key]
}

const selectEditorDay = async (dayKey) => {
  activeEditorDayKey.value = dayKey
  resourceSearchQuery.value = ''
  await nextTick()
  editorDayRefs.value[dayKey]?.scrollIntoView?.({
    behavior: 'smooth',
    block: 'nearest',
  })
}

const getDayGroupShiftCount = (group) => group?.slotGroups?.length ?? 0

const getDayGroupDateDraft = (group) =>
  dayDateDrafts.value[group?.renderKey] ?? normalizeIsoDate(group?.date)

const getDayGroupDisplayDate = (group) => {
  const normalizedDraft = normalizeIsoDate(getDayGroupDateDraft(group))
  if (normalizedDraft) {
    return normalizedDraft
  }

  return normalizeIsoDate(group?.date)
}

const getDayGroupLabel = (group, format = 'dddd DD MMM YYYY') => {
  const displayDate = getDayGroupDisplayDate(group)
  return displayDate ? dayjs(displayDate).format(format) : 'Invalid date'
}

const setDayGroupDateDraft = (group, value) => {
  if (!group?.renderKey) {
    return
  }

  const drafts = {
    ...dayDateDrafts.value,
    [group.renderKey]: value,
  }
  if (normalizeIsoDate(value) === normalizeIsoDate(group.date)) {
    delete drafts[group.renderKey]
  }
  dayDateDrafts.value = drafts
  emit('schedule-dirty', Object.keys(drafts).length > 0)
}

const applyPendingDayDateDrafts = () => {
  let hasInvalidDraft = false

  editorDayGroups.value.forEach((group) => {
    const draftValue = dayDateDrafts.value[group?.renderKey]
    if (draftValue === undefined) {
      return
    }

    const normalizedDraft = normalizeIsoDate(draftValue)
    if (!normalizedDraft) {
      hasInvalidDraft = true
      return
    }

    ;(group?.slotGroups ?? []).forEach((slotGroup) => {
      ;(slotGroup?.entries ?? []).forEach((entry) => {
        entry.date = normalizedDraft
      })
    })
  })

  if (!hasInvalidDraft) {
    dayDateDrafts.value = {}
    emit('schedule-dirty', false)
  }

  return { invalid: hasInvalidDraft }
}

const discardPendingDayDateDrafts = () => {
  dayDateDrafts.value = {}
  emit('schedule-dirty', false)
}

const getPendingDayDateDrafts = () => ({ ...dayDateDrafts.value })

const restorePendingDayDateDrafts = (drafts = {}) => {
  dayDateDrafts.value = { ...drafts }
  emit('schedule-dirty', Object.keys(dayDateDrafts.value).length > 0)
}

defineExpose({
  applyPendingDayDateDrafts,
  discardPendingDayDateDrafts,
  getPendingDayDateDrafts,
  restorePendingDayDateDrafts,
})

const getEntryTimeLabel = (entry) => `${entry?.startTime?.slice(0, 5)} - ${entry?.finishTime?.slice(0, 5)}`
const getSlotGroupResourceCount = (slotGroup) => slotGroup?.resources?.length ?? 0

const getFilledPersonnelCount = (resource) =>
  (resource?.personnel_assignments ?? []).filter(assignment => assignment?.staff_id ?? assignment?.staff?.id).length

const getResourceRequiredPersonnelCount = (resource) => getPersonnelSlots(resource).length

const getResourcesForDayGroup = (group) =>
  (group?.slotGroups ?? []).flatMap(slotGroup => slotGroup?.resources ?? [])

const getDayCoverage = (group) => {
  const resources = getResourcesForDayGroup(group)
  return resources.reduce(
    (coverage, resource) => ({
      filled: coverage.filled + getFilledPersonnelCount(resource),
      required: coverage.required + getResourceRequiredPersonnelCount(resource),
    }),
    { filled: 0, required: 0 },
  )
}

const resourceMatchesFilters = (resource) => {
  if (
    showUnfilledOnly.value
    && getFilledPersonnelCount(resource) >= getResourceRequiredPersonnelCount(resource)
  ) {
    return false
  }

  const query = resourceSearchQuery.value.trim().toLowerCase()
  if (!query) return true

  const staffNames = (resource?.personnel_assignments ?? [])
    .map(assignment => assignment?.staff?.contact?.name ?? '')
    .join(' ')
  return `${getProductName(resource?.product_id)} ${staffNames}`
    .toLowerCase()
    .includes(query)
}

const getEditorResourceGroups = (slotGroup) => {
  const groups = new Map()
  ;(slotGroup?.resources ?? []).forEach((resource, index) => {
    if (!resourceMatchesFilters(resource)) return
    const productId = resource?.product_id ?? `unknown-${index}`
    const key = `${slotGroup?.renderKey ?? 'shift'}-${productId}`
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        productId,
        productName: getProductName(resource?.product_id),
        resources: [],
        filled: 0,
        required: 0,
      })
    }
    const group = groups.get(key)
    group.resources.push({ resource, originalIndex: index })
    group.filled += getFilledPersonnelCount(resource)
    group.required += getResourceRequiredPersonnelCount(resource)
  })
  return Array.from(groups.values())
}

const isResourceGroupExpanded = (group) =>
  expandedResourceGroups.value[group?.key] ?? group?.resources?.length <= 4

const toggleResourceGroup = (group) => {
  expandedResourceGroups.value = {
    ...expandedResourceGroups.value,
    [group.key]: !isResourceGroupExpanded(group),
  }
}

const getResourceCoverageLabel = (resource) => {
  const totalSlots = getPersonnelSlots(resource).length
  if (!totalSlots) {
    return 'No crew required'
  }

  return `${getFilledPersonnelCount(resource)} / ${totalSlots} crew assigned`
}

const getResourceCoverageTone = (resource) => {
  const totalSlots = getPersonnelSlots(resource).length
  if (!totalSlots) {
    return 'text-slate-500 dark:text-slate-400'
  }

  return getFilledPersonnelCount(resource) >= totalSlots
    ? 'text-emerald-600 dark:text-emerald-300'
    : 'text-amber-600 dark:text-amber-300'
}

const RESOURCE_TONE_CLASSES = [
  'planner-bar--emerald',
  'planner-bar--rose',
  'planner-bar--violet',
  'planner-bar--cyan',
  'planner-bar--amber',
  'planner-bar--fuchsia',
]

const timelineEndMinutes = computed(() => getTimelineEndMinutes(slotGroups.value))
const timelineHours = computed(() => buildTimelineHours(timelineEndMinutes.value))
const timelineMobileMarkers = computed(() => buildTimelineMarkers(timelineEndMinutes.value))
const timelineStyle = computed(() => ({
  '--planner-hour-count': timelineHours.value.length,
}))

const getTimelineBarStyle = (entry) =>
  buildTimelineBarStyle(entry, timelineEndMinutes.value)

const getTimelineMarkerStyle = (marker) => {
  const left = (marker.minutes / timelineEndMinutes.value) * 100
  let transform = 'translateX(-50%)'

  if (marker.minutes <= 0) {
    transform = 'translateX(0)'
  } else if (marker.minutes >= timelineEndMinutes.value) {
    transform = 'translateX(-100%)'
  }

  return {
    left: `${left}%`,
    transform,
  }
}

const getTimelineToneClass = (resource) => {
  const toneSeed = Number(resource?.product_id ?? resource?.id ?? 0)
  return RESOURCE_TONE_CLASSES[Math.abs(toneSeed) % RESOURCE_TONE_CLASSES.length]
}

const getResourceQuantity = (resource) => {
  const quantity = Number.parseInt(resource?.quantity, 10)
  return quantity > 0 ? quantity : 1
}

const getResourceAssignmentNames = (resource) =>
  (resource?.personnel_assignments ?? [])
    .map((assignment) => assignment?.staff?.contact?.name)
    .filter(Boolean)

const getTimelineResourceLabel = (group) => {
  const quantity = group?.displayQuantity ?? getResourceQuantity(group?.resource)
  return `${quantity} x ${getProductName(group?.resource?.product_id)}`
}

const getTimelineResourceMeta = (group) => {
  const names = group?.crewNames ?? getResourceAssignmentNames(group?.resource)
  if (names.length) {
    return names.join(', ')
  }

  const requirementCount = getPersonnelSlots(group?.resource).length
  if (requirementCount) {
    return `${requirementCount} crew slot${requirementCount > 1 ? 's' : ''} unassigned`
  }

  return 'No crew assigned'
}

const getTimelineRowsForResources = (resources) =>
  Array.isArray(resources) && resources.length
    ? Array.from(
        resources.reduce((groups, resource, index) => {
          const key = String(resource?.product_id ?? resource?.id ?? `resource-${index}`)
          if (!groups.has(key)) {
            groups.set(key, {
              key,
              resource,
              resources: [],
              quantities: [],
              crewNames: new Set(),
            })
          }

          const group = groups.get(key)
          group.resources.push(resource)
          group.quantities.push(getResourceQuantity(resource))
          getResourceAssignmentNames(resource).forEach((name) => group.crewNames.add(name))

          return groups
        }, new Map()).values()
      ).map((group) => ({
        ...group,
        crewNames: Array.from(group.crewNames),
        displayQuantity: group.quantities.reduce(
          (total, quantity) => total + quantity,
          0,
        ),
      }))
    : []

const getTimelineRowsForSlotGroup = (slotGroup) => getTimelineRowsForResources(slotGroup?.resources)
const getEntryResourceCount = (entry) => getTimelineRowsForResources(entry?.resources).length

const getSlotGroupPlannerKey = (slotGroup, slotGroupIndex = 0) =>
  slotGroup?.renderKey ?? slotGroup?.matchKey ?? `planner-slot-group-${slotGroupIndex}`

const getSlotGroupScheduleLabel = (slotGroup) =>
  `${slotGroup?.startTime?.slice(0, 5) ?? '--:--'} - ${slotGroup?.finishTime?.slice(0, 5) ?? '--:--'}`

const openResourceTimetableDocument = async () => {
  if (!referenceData.value?.id) {
    return
  }

  const printWindow = window.open('', '_blank')

  try {
    const response = await apiClient.get(
      `/api/reference/${referenceData.value.id}/resource-timetable-document`,
      { responseType: 'blob' }
    )

    const sourceBlob = response.data
    const pdfBlob =
      sourceBlob.type === 'application/pdf'
        ? sourceBlob
        : new Blob([sourceBlob], { type: 'application/pdf' })
    const objectUrl = window.URL.createObjectURL(pdfBlob)

    if (printWindow) {
      printWindow.location.href = objectUrl
    } else {
      window.open(objectUrl, '_blank')
    }

    setTimeout(() => window.URL.revokeObjectURL(objectUrl), 60_000)
  } catch (error) {
    if (printWindow) {
      printWindow.close()
    }
    console.error(error)
    alert(error?.response?.data?.message ?? 'Unable to open printable timetable.')
  }
}

const dateTime = ref({
  startDate: dayjs().format('YYYY-MM-DD'),
  finishDate: dayjs().format('YYYY-MM-DD'),
  startTime: '08:00:00',
  finishTime: '18:00:00',
  qty: 2,
  product: null
})

const entryRefs = ref({})
const resourceRefs = ref({})
const RESOURCE_FLASH_CLASS = 'flash-added-resource'
const ENTRY_FLASH_CLASS = 'flash-added-entry'

const getEntryRenderKey = (entry, entryIndex = null) =>
  entry.id ?? entry.date_id ?? `${entry.date}-${entry.startTime}-${entry.finishTime}-${entryIndex ?? 0}`

const getResourceRenderKey = (entry, resource, resourceIndex = null) =>
  resource.id ?? `${getEntryRenderKey(entry)}-${resourceIndex ?? 0}`

const setEntryRef = (key, element) => {
  if (!key) return
  if (element) {
    entryRefs.value[key] = element
    return
  }
  delete entryRefs.value[key]
}

const setResourceRef = (key, element) => {
  if (!key) return
  if (element) {
    resourceRefs.value[key] = element
    return
  }
  delete resourceRefs.value[key]
}

const flashElement = (element, className) => {
  if (!element) return
  element.classList.remove(className)
  // Restart animation if same row is added repeatedly.
  void element.offsetWidth
  element.classList.add(className)
  setTimeout(() => element.classList.remove(className), 1600)
}

const focusAndFlashNewEntries = async (addedEntries, addedResources) => {
  await nextTick()

  const entryKeys = addedEntries
    .map((entry) => getSlotGroupMatchKey(entry))
    .filter(Boolean)

  const resourceKeys = addedResources
    .map(({ entry, resource }) => {
      const resourceIndex = entry?.resources?.indexOf(resource) ?? -1
      return resourceIndex >= 0 ? getResourceRenderKey(entry, resource, resourceIndex) : null
    })
    .filter(Boolean)

  entryKeys.forEach((key) => {
    flashElement(entryRefs.value[key], ENTRY_FLASH_CLASS)
  })
  resourceKeys.forEach((key) => {
    flashElement(resourceRefs.value[key], RESOURCE_FLASH_CLASS)
  })

  const firstResourceKey = resourceKeys[0]
  const firstEntryKey = entryKeys[0]
  const targetRow = firstResourceKey ? resourceRefs.value[firstResourceKey] : entryRefs.value[firstEntryKey]
  if (!targetRow) return

  targetRow.scrollIntoView({ behavior: 'smooth', block: 'center' })
  const focusTarget = targetRow.querySelector('select, input, button, textarea')
  if (focusTarget && typeof focusTarget.focus === 'function') {
    focusTarget.focus({ preventScroll: true })
  }
}

function addEntry(includeResources = true) {
  // ensure finish ≥ start
  if (dateTime.value.finishDate < dateTime.value.startDate) {
    dateTime.value.finishDate = dateTime.value.startDate
  }
  const { startDate, finishDate, startTime, finishTime, qty, product: prodId } = dateTime.value
  const rangeDays = dayjs(finishDate).diff(dayjs(startDate), 'day') + 1
  if (rangeDays > 7) {
    const proceed = confirm(`You are about to add entries for ${rangeDays} days. Continue?`)
    if (!proceed) {
      return
    }
  }
  const quantity = Math.max(0, Number.parseInt(qty, 10) || 0)
  const baseProduct = includeResources
    ? products.value.find(p => Number(p.id) === Number(prodId))
    : null
  if (includeResources && !baseProduct) return
  if (includeResources && !quantity) return
  if (!Array.isArray(entries.value?.dates)) {
    entries.value.dates = []
  }
  const addedEntries = []
  const addedResources = []

  for (let d = new Date(startDate); d <= new Date(finishDate); d.setDate(d.getDate() + 1)) {
    const yyyy = d.getFullYear()
    const mm = String(d.getMonth() + 1).padStart(2, '0')
    const dd = String(d.getDate()).padStart(2, '0')
    const date = `${yyyy}-${mm}-${dd}`

    const existingEntry = entries.value.dates.find(entry =>
      entry.date === date &&
      entry.startTime === startTime &&
      entry.finishTime === finishTime
    )

    const entry = existingEntry ?? {
      date,
      id: null,
      date_id: null,
      startTime,
      finishTime,
      resources: [],
    }
    if (!existingEntry) {
      entries.value.dates.push(entry)
      addedEntries.push(entry)
    }
    if (includeResources) {
      for (let i = 0; i < quantity; i++) {
        const resource = addResource(entry, baseProduct.id)
        if (resource) {
          addedResources.push({ entry, resource })
        }
      }
    }
  }

  focusAndFlashNewEntries(addedEntries, addedResources)
}

function removeEntry(dateEntry) {
  entries.value.dates = entries.value.dates.filter(d => d !== dateEntry)
}

function removeSlotGroup(slotGroup) {
  const slotEntries = new Set(slotGroup?.entries ?? [])
  entries.value.dates = (entries.value?.dates ?? []).filter(entry => !slotEntries.has(entry))
}

function addResource(entry, productId = null) {
  const selectedProductId = Number(productId ?? dateTime.value.product)
  const baseProduct = products.value.find(p => Number(p.id) === selectedProductId)
  if (!baseProduct) return
  const clone = JSON.parse(JSON.stringify(baseProduct))
  const newResource = {
    product: clone,
    product_id: clone.id,
    quantity: 1,
    staff: null,
    personnel_assignments: [],
  }
  entry.resources.push(newResource)
  return newResource
}

function removeResource(resource) {
  for (const dateEntry of entries.value.dates) {
    const idx = dateEntry.resources.indexOf(resource)
    if (idx !== -1) {
      dateEntry.resources.splice(idx, 1)
      break
    }
  }
}

function addResourceToSlotGroup(slotGroup, productId = null) {
  const targetEntry = slotGroup?.entries?.[0]
  if (!targetEntry) {
    return
  }
  const resource = addResource(targetEntry, productId)
  if (resource) {
    const groupKey = `${slotGroup?.renderKey ?? 'shift'}-${resource.product_id}`
    expandedResourceGroups.value = {
      ...expandedResourceGroups.value,
      [groupKey]: true,
    }
  }
  return resource
}

function updateSlotGroupStartTime(slotGroup, value) {
  ;(slotGroup?.entries ?? []).forEach((entry) => {
    entry.startTime = value
  })
}

function updateSlotGroupFinishTime(slotGroup, value) {
  ;(slotGroup?.entries ?? []).forEach((entry) => {
    entry.finishTime = value
  })
}

function updateSlotGroupLocationName(slotGroup, value) {
  ;(slotGroup?.entries ?? []).forEach((entry) => {
    entry.location_name = value || null
  })
}

function updateSlotGroupAddress(slotGroup, value) {
  const nextAddress = normalizeAddressText(value)
  const selectedAddress = addressOptions.value.find(
    (option) => normalizeAddressKey(option.data) === normalizeAddressKey(nextAddress),
  )
  ;(slotGroup?.entries ?? []).forEach((entry) => {
    entry.location_address = nextAddress || null
    entry.address_id = selectedAddress?.address_id ?? null
    entry.address = selectedAddress?.address_id
      ? {
        id: selectedAddress.address_id,
        data: selectedAddress.data,
        latitude: selectedAddress.latitude ?? null,
        longitude: selectedAddress.longitude ?? null,
      }
      : null
  })
}

const getStaffActionKey = (resource, personnel = null) =>
  `${resource?.id ?? 'draft'}-${personnel?.product_personnel_requirement_id ?? 'resource'}`

const setStaffBusy = (key, isBusy) => {
  staffActionState.value = {
    ...staffActionState.value,
    [key]: isBusy,
  }
}

const isStaffBusy = (resource, personnel) =>
  Boolean(staffActionState.value[getStaffActionKey(resource, personnel)])

const setStaffActionError = (resource, message = '') => {
  staffActionErrors.value = {
    ...staffActionErrors.value,
    [getStaffActionKey(resource)]: message,
  }
}

const getStaffActionError = (resource) =>
  staffActionErrors.value[getStaffActionKey(resource)] ?? ''

function appendStaffAssignment(resource, personnel, staff, persistedAssignment = {}) {
  resource.personnel_assignments.push({
    ...persistedAssignment,
    product_personnel_requirement_id: personnel.product_personnel_requirement_id,
    staff_id: staff.id,
    staff: persistedAssignment.staff ?? staff,
  })
}

async function addStaffResource(resource, personnel, staff) {
  if (isStaffAssigned(resource, staff)) {
    alert(`${staff.contact.name} has already been assigned!`)
    return
  }

  if (!resource.id) {
    appendStaffAssignment(resource, personnel, staff)
    return
  }

  const actionKey = getStaffActionKey(resource, personnel)
  if (staffActionState.value[actionKey]) return
  if (!referenceStore.referenceService) {
    setStaffActionError(resource, 'Unable to save this crew assignment. Reload the job and try again.')
    return
  }

  setStaffBusy(actionKey, true)
  setStaffActionError(resource)
  try {
    const response = await referenceStore.referenceService.allocateStaff(
      resource.id,
      personnel.product_personnel_requirement_id,
      staff.id,
      { suppressAutoReload: true, suppressErrorAlert: true },
    )
    const assignment = response?.data?.assignment
    if (!assignment?.id) {
      throw new Error('The crew assignment was not returned by the server.')
    }
    appendStaffAssignment(resource, personnel, staff, assignment)
    showToast(`${staff.contact?.name ?? 'Crew member'} assigned. Changes saved.`)
  } catch (error) {
    console.error(error)
    setStaffActionError(
      resource,
      getApiErrorMessage(error, 'Unable to assign this crew member.'),
    )
  } finally {
    setStaffBusy(actionKey, false)
  }
}

function isStaffAssigned(resource, staff) {
  if (!Array.isArray(resource.personnel_assignments)) {
    return false;
  }

  // Look for any assignment whose staff_id matches
  return resource.personnel_assignments.some(
    assignment => assignment.staff_id === staff.id
  );
}

function changeProduct(resource, productId) {
  const base = products.value.find(p => p.id === Number(productId))
  if (!base) return
  resource.product = JSON.parse(JSON.stringify(base))
}

const scheduleUnitQuantity = (group) => group.resources.reduce(
  (total, item) => total + getResourceQuantity(item.resource), 0,
)

const updateScheduleUnitType = (group, value) => {
  if (group.filled) {
    alert('Unassign the crew from these units before changing their type.')
    return
  }
  group.resources.forEach(({ resource }) => {
    resource.product_id = Number(value)
    changeProduct(resource, value)
  })
}

const updateScheduleUnitQuantity = (slotGroup, group, value) => {
  const quantity = Number(value)
  if (!Number.isInteger(quantity) || quantity < 0) return
  const current = scheduleUnitQuantity(group)
  if (quantity > current) {
    for (let i = current; i < quantity; i++) addResource(slotGroup.entries[0], group.productId)
  } else {
    let remaining = current - quantity
    for (const { resource } of [...group.resources].reverse()) {
      if (!remaining) break
      const count = getResourceQuantity(resource)
      const assignments = (resource.personnel_assignments ?? []).filter(a => a.staff_id || a.staff)
      const requirements = getProduct(resource.product_id)?.personnel_requirements ?? []
      // Preserve enough units for every crew requirement, including multi-person units.
      const occupied = assignments.reduce((minimum, assignment) => {
        const requirement = requirements.find(req => Number(req.id) === Number(assignment.product_personnel_requirement_id))
        if (!requirement) return count
        const assigned = assignments.filter(a => Number(a.product_personnel_requirement_id) === Number(requirement.id)).length
        return Math.max(minimum, Math.ceil(assigned / Math.max(1, Number(requirement.required_count) || 1)))
      }, 0)
      const removable = Math.min(remaining, Math.max(0, count - occupied))
      if (!removable) continue
      if (removable === count) removeResource(resource)
      else resource.quantity = count - removable
      remaining -= removable
    }
    if (remaining) {
      const removed = current - quantity - remaining
      showToast(`Removed ${removed} empty slot${removed === 1 ? '' : 's'}. Couldn't remove ${remaining} slot${remaining === 1 ? '' : 's'} due to allocation.`, 'warning')
    }
  }
}

const getPersonnelSlots = (resource) => {
  const reqs = getProduct(resource.product_id)?.personnel_requirements || [];
  const assigns = resource.personnel_assignments || [];
  const resourceQuantity = getResourceQuantity(resource);

  return reqs.flatMap(req => {
    // existing filled slots for this requirement
    const existing = assigns
      .filter(a => Number(a.product_personnel_requirement_id) === Number(req.id))
      .map(a => ({
        ...a,
        skill: req.skill,
        staff_skill_id: req.staff_skill_id
      }));

    // Each resource unit needs the configured crew requirement.
    const requiredSlots = resourceQuantity * Math.max(1, Number(req.required_count) || 1);
    const slots = [...existing];
    for (let i = existing.length; i < requiredSlots; i++) {
      slots.push({
        id: null,
        product_personnel_requirement_id: req.id,
        staff_id: null,
        staff: null,
        skill: req.skill,
        staff_skill_id: req.staff_skill_id
      });
    }

    return slots;
  });
};

const clearAssignment = (resource, personnel) => {
  // Remove from resource.personnel_assignments
  const idx = resource.personnel_assignments.findIndex(a =>
    (personnel.id && a.id === personnel.id) ||
    (!personnel.id && a.product_personnel_requirement_id === personnel.product_personnel_requirement_id && a.staff_id === personnel.staff_id)
  );
  if (idx !== -1) {
    resource.personnel_assignments.splice(idx, 1);
  }
}

const unassignStaff = async (resource, personnel) => {
  if (!resource || !personnel) {
    return;
  }
  if (!resource.id || !personnel.id || !referenceStore.referenceService) {
    clearAssignment(resource, personnel);
    return;
  }

  try {
    const staffName = personnel.staff?.contact?.name ?? 'Crew member'
    await referenceStore.referenceService.allocateStaff(
      personnel.job_resource_id ?? resource.id,
      personnel.product_personnel_requirement_id,
      null,
      {
        assignmentId: personnel.id,
        suppressAutoReload: true,
        suppressErrorAlert: true,
      },
    );
    setStaffActionError(resource)
    clearAssignment(resource, personnel);
    showToast(`${staffName} removed. Changes saved.`)
  } catch (error) {
    console.error(error);
    setStaffActionError(
      resource,
      getApiErrorMessage(error, 'Unable to unassign this crew member.'),
    )
  }
}


</script>

<template>

  <FormBox v-show="!reference.isLoading" :show-header="!embedded">
    <template #header>
      {{ isScheduleMode ? 'Schedule management' : 'Resources' }}
    </template>
    <template #nav>
      <button
        v-if="isResourceMode && referenceData?.id"
        type="button"
        class="btn action"
        @click.prevent="openResourceTimetableDocument"
      >
        <PrinterIcon class="icon" />
        <span class="hidden md:inline-block">Print</span>
      </button>
    </template>
    <div class="space-y-8">
      <section v-if="isResourceMode" class="rounded border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
        <div class="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 class="font-semibold text-slate-900 dark:text-slate-100">Event Lead</h2>
            <p class="mt-1 text-xs text-slate-500 dark:text-slate-400">
              Used in the customer relationship check-in seven days before the event.
            </p>
          </div>
          <span
            class="rounded-full px-2 py-1 text-xs font-semibold"
            :class="referenceData?.lead_staff_id ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200' : 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-200'"
          >
            {{ editable ? (leadSaving ? 'Saving…' : 'Saved instantly') : (referenceData?.lead_staff_id ? 'Selected' : 'Required') }}
          </span>
        </div>
        <select
          v-if="editable"
          :value="referenceData?.lead_staff_id ?? ''"
          class="mt-3 w-full rounded border border-gray-300 bg-white p-2 text-sm dark:border-gray-600 dark:bg-gray-700"
          :disabled="leadSaving"
          @change="updateEventLead($event.target.value)"
        >
          <option value="">No event lead selected</option>
          <option v-for="staff in assignedStaffOptions" :key="`event-lead-${staff.id}`" :value="staff.id">
            {{ staff.contact?.name ?? `Staff #${staff.id}` }}
          </option>
        </select>
        <div
          v-else
          class="mt-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 dark:border-slate-700 dark:bg-slate-800/70"
        >
          <div class="text-sm font-semibold text-slate-900 dark:text-slate-100">
            {{ referenceData?.lead_staff?.contact?.name ?? 'No event lead selected' }}
          </div>
          <div v-if="referenceData?.lead_staff?.contact?.phone" class="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
            {{ referenceData.lead_staff.contact.phone }}
          </div>
        </div>
        <p v-if="editable && !assignedStaffOptions.length" class="mt-2 text-xs text-amber-600 dark:text-amber-300">
          Allocate staff to the job before selecting an event lead.
        </p>
      </section>

      <!-- Add by Date Range -->
      <section class="
 bg-white dark:bg-slate-800
    rounded
    shadow-md shadow-gray-300
    dark:shadow-md dark:shadow-gray-600/50
    p-2 space-y-4

      " v-if="isScheduleMode">

        <form class="grid grid-cols-1 gap-4 text-sm sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6">
          <div>
            <label class="block font-medium mb-1">Start Date</label>
            <input type="date" v-model="dateTime.startDate"
              class="w-full p-2 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700">
          </div>
          <div>
            <label class="block font-medium mb-1">End Date</label>
            <input type="date" v-model="dateTime.finishDate"
              class="w-full p-2 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700">
          </div>
          <div>
            <label class="block font-medium mb-1">Start Time</label>
            <input type="time" v-model="dateTime.startTime"
              class="w-full p-2 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700">
          </div>
          <div>
            <label class="block font-medium mb-1">End Time</label>
            <input type="time" v-model="dateTime.finishTime"
              class="w-full p-2 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700">
          </div>
          <div>
            <label class="block font-medium mb-1">Unit Type</label>
            <select class="w-full p-2 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
              v-model="dateTime.product">
              <option value="">-- Select Unit --</option>
              <option :key="product.id" v-for="product in products" :value="product.id">{{ product.name }}</option>
            </select>
          </div>
          <div>
            <label class="block font-medium mb-1">Unit Qty</label>
            <input type="number" v-model="dateTime.qty">
          </div>
        </form>

        <div class="mt-2 flex flex-wrap items-center gap-2">
          <button type="button" class="bg-m1 text-white px-4 py-2 rounded" @click.prevent="addEntry(true)">Add Date/Time Slot</button>
        </div>
        <div class="text-xs text-slate-500">
          Create shifts with unit types and quantities. Each unit supplies the staff slots for allocation below.
        </div>
      </section>

      <section v-if="isResourceMode && !editable" class="space-y-4">
        <div class="planner-shell planner-shell--desktop" :style="timelineStyle">
          <div class="planner-header-row">
            <div class="planner-date-header">Date</div>
            <div class="planner-hours-header">
              <div v-for="hour in timelineHours" :key="`hour-${hour.index}`" class="planner-hour-cell">
                {{ hour.label }}
              </div>
            </div>
          </div>

          <div v-if="slotGroups.length" class="planner-body">
            <div
              v-for="(slotGroup, slotGroupIndex) in slotGroups"
              :key="`planner-${getSlotGroupPlannerKey(slotGroup, slotGroupIndex)}`"
              class="planner-entry-row"
            >
              <div class="planner-date-cell">
                <div class="planner-date-title">{{ dayjs(slotGroup.date).format('DD MMM YYYY') }}</div>
                <div class="planner-date-subtitle">{{ dayjs(slotGroup.date).format('dddd') }}</div>
                <div class="planner-date-meta">
                  {{ getSlotGroupScheduleLabel(slotGroup) }}
                </div>
                <div class="planner-date-meta">
                  {{ getDuration(slotGroup.startTime, slotGroup.finishTime) }} ·
                  {{ getTimelineRowsForSlotGroup(slotGroup).length }} resource{{ getTimelineRowsForSlotGroup(slotGroup).length === 1 ? '' : 's' }} ·
                  {{ getVehicleAllocationsForSlotGroup(slotGroup).length }} vehicle{{ getVehicleAllocationsForSlotGroup(slotGroup).length === 1 ? '' : 's' }}
                </div>
                <div
                  v-if="slotGroup.location_name || slotGroup.location_address"
                  class="planner-date-meta"
                >
                  {{ [slotGroup.location_name, slotGroup.location_address].filter(Boolean).join(' · ') }}
                </div>
                <div
                  v-for="allocation in getVehicleAllocationsForSlotGroup(slotGroup)"
                  :key="`planner-vehicle-${allocation.id}`"
                  class="planner-date-meta"
                >
                  {{ getAllocationVehicleLabel(allocation) }} · {{ allocation.driver?.contact?.name ?? 'No driver' }}
                </div>
              </div>

              <div class="planner-timeline-cell">
                <div class="planner-grid-backdrop"></div>

                <template v-if="getTimelineRowsForSlotGroup(slotGroup).length">
                  <div
                    v-for="(group, resourceIndex) in getTimelineRowsForSlotGroup(slotGroup)"
                    :key="`planner-resource-${getSlotGroupPlannerKey(slotGroup, slotGroupIndex)}-${group.key}-${resourceIndex}`"
                    class="planner-track"
                  >
                    <div
                      class="planner-bar"
                      :class="getTimelineToneClass(group.resource)"
                      :style="getTimelineBarStyle(slotGroup)"
                    >
                      <span class="planner-bar-title">{{ getTimelineResourceLabel(group) }}</span>
                      <span class="planner-bar-meta">{{ getTimelineResourceMeta(group) }}</span>
                    </div>
                  </div>
                </template>

                <div v-else class="planner-empty-state">
                  No resources scheduled for this date.
                </div>
              </div>
            </div>
          </div>

          <div v-else class="planner-empty-board">
            Add a date range above to start building the resource planner.
          </div>
        </div>

        <div class="planner-mobile-list" :style="timelineStyle">
          <div v-if="!slotGroups.length" class="planner-mobile-empty">
            Add a date range above to start building the resource planner.
          </div>

          <template v-else>
            <article
              v-for="(slotGroup, slotGroupIndex) in slotGroups"
              :key="`planner-mobile-${getSlotGroupPlannerKey(slotGroup, slotGroupIndex)}`"
              class="planner-mobile-card"
            >
              <div class="planner-mobile-card__header">
                <div>
                  <div class="planner-mobile-card__title">{{ dayjs(slotGroup.date).format('DD MMM YYYY') }}</div>
                  <div class="planner-mobile-card__subtitle">{{ dayjs(slotGroup.date).format('dddd') }}</div>
                </div>
                <div class="planner-mobile-card__summary">
                  {{ getTimelineRowsForSlotGroup(slotGroup).length }} resource{{ getTimelineRowsForSlotGroup(slotGroup).length === 1 ? '' : 's' }} ·
                  {{ getVehicleAllocationsForSlotGroup(slotGroup).length }} vehicle{{ getVehicleAllocationsForSlotGroup(slotGroup).length === 1 ? '' : 's' }}
                </div>
              </div>

              <div class="planner-mobile-card__chips">
                <span class="planner-mobile-chip">{{ getSlotGroupScheduleLabel(slotGroup) }}</span>
                <span class="planner-mobile-chip">{{ getDuration(slotGroup.startTime, slotGroup.finishTime) }}</span>
              </div>

              <div
                v-if="slotGroup.location_name || slotGroup.location_address"
                class="planner-mobile-card__location"
              >
                {{ [slotGroup.location_name, slotGroup.location_address].filter(Boolean).join(' · ') }}
              </div>

              <div
                v-if="getVehicleAllocationsForSlotGroup(slotGroup).length"
                class="mt-3 grid gap-2"
              >
                <div
                  v-for="allocation in getVehicleAllocationsForSlotGroup(slotGroup)"
                  :key="`planner-mobile-vehicle-${allocation.id}`"
                  class="rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-900 dark:border-sky-800 dark:bg-sky-950/30 dark:text-sky-100"
                >
                  <span class="font-semibold">{{ getAllocationVehicleLabel(allocation) }}</span>
                  <span> · {{ allocation.driver?.contact?.name ?? 'No driver' }}</span>
                </div>
              </div>

              <div class="planner-mobile-axis">
                <span
                  v-for="marker in timelineMobileMarkers"
                  :key="`planner-mobile-hour-${getSlotGroupPlannerKey(slotGroup, slotGroupIndex)}-${marker.index}`"
                  :style="getTimelineMarkerStyle(marker)"
                >
                  {{ marker.label }}
                </span>
              </div>

              <div v-if="getTimelineRowsForSlotGroup(slotGroup).length" class="planner-mobile-resource-list">
                <div
                  v-for="(group, resourceIndex) in getTimelineRowsForSlotGroup(slotGroup)"
                  :key="`planner-mobile-resource-${getSlotGroupPlannerKey(slotGroup, slotGroupIndex)}-${group.key}-${resourceIndex}`"
                  class="planner-mobile-resource"
                >
                  <div class="planner-mobile-resource__labels">
                    <span class="planner-mobile-resource__title">{{ getTimelineResourceLabel(group) }}</span>
                    <span class="planner-mobile-resource__meta">{{ getTimelineResourceMeta(group) }}</span>
                  </div>

                  <div class="planner-mobile-track">
                    <div class="planner-mobile-grid-backdrop"></div>
                    <div
                      class="planner-bar planner-bar--mobile"
                      :class="getTimelineToneClass(group.resource)"
                      :style="getTimelineBarStyle(slotGroup)"
                    >
                      <span class="planner-bar-title">{{ getSlotGroupScheduleLabel(slotGroup) }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div v-else class="planner-empty-state planner-empty-state--mobile">
                No resources scheduled for this date.
              </div>
            </article>
          </template>
        </div>
      </section>

      <div v-if="(isScheduleMode || editable) && editorDayGroups.length" class="resource-workspace">
        <div v-if="editorDayGroups.length" class="editor-day-nav">
          <div>
            <div class="editor-day-nav__label">Resource days</div>
            <p class="mt-1 text-xs text-slate-500 dark:text-slate-400">
              Select one day to keep large jobs manageable.
            </p>
          </div>
          <div class="editor-day-nav__chips">
            <button
              v-for="group in editorDayGroups"
              :key="`editor-day-chip-${group.renderKey}`"
              type="button"
              class="editor-day-chip"
              :class="{ 'editor-day-chip--active': activeEditorDay?.renderKey === group.renderKey }"
              @click.prevent="selectEditorDay(group.renderKey)"
            >
              <span>{{ getDayGroupLabel(group, 'ddd DD MMM') }}</span>
              <span>
                {{ getDayCoverage(group).filled }} / {{ getDayCoverage(group).required }} crew ·
                {{ group.resourceCount }} unit{{ group.resourceCount === 1 ? '' : 's' }}
              </span>
            </button>
          </div>
        </div>

        <section
          v-if="activeEditorDay"
          :key="`editor-day-${activeEditorDay.renderKey}`"
          :ref="el => setEditorDayRef(activeEditorDay.renderKey, el)"
          data-testid="human-resource-day"
          class="editor-day-group"
        >
          <div class="editor-day-group__toggle">
            <div class="editor-day-group__summary">
              <div class="editor-day-group__eyebrow">Active day</div>
              <h2 class="editor-day-group__title">{{ getDayGroupLabel(activeEditorDay) }}</h2>
              <div class="editor-day-group__meta">
                <span>{{ getDayGroupShiftCount(activeEditorDay) }} shift{{ getDayGroupShiftCount(activeEditorDay) === 1 ? '' : 's' }}</span>
                <span>{{ activeEditorDay.resourceCount }} unit{{ activeEditorDay.resourceCount === 1 ? '' : 's' }}</span>
                <span>{{ getDayCoverage(activeEditorDay).filled }} / {{ getDayCoverage(activeEditorDay).required }} crew</span>
                <span>{{ getVehicleCountForDayGroup(activeEditorDay) }} vehicle{{ getVehicleCountForDayGroup(activeEditorDay) === 1 ? '' : 's' }}</span>
              </div>
            </div>

            <span
              class="rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-800 dark:bg-amber-950/50 dark:text-amber-200"
            >
              {{ isScheduleMode ? 'Schedule changes save automatically' : 'Edit mode · allocations save instantly' }}
            </span>
          </div>

          <div class="space-y-4">
            <div v-if="isResourceMode" class="resource-editor-filters">
              <label class="min-w-0 flex-1">
                <span class="sr-only">Filter resources or assigned staff</span>
                <input
                  v-model="resourceSearchQuery"
                  type="search"
                  class="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-800"
                  placeholder="Filter unit type or assigned staff…"
                >
              </label>
              <label class="inline-flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-200">
                <input v-model="showUnfilledOnly" type="checkbox">
                Unfilled only
              </label>
            </div>

            <div v-if="isScheduleMode" class="grid gap-3 rounded-2xl border border-slate-200 bg-white/80 p-4 dark:border-slate-700 dark:bg-slate-800/60 md:max-w-sm">
              <div>
                <label class="block text-xs font-semibold uppercase tracking-wide text-slate-400">Day Date</label>
                <input
                  type="date"
                  :value="getDayGroupDateDraft(activeEditorDay)"
                  :disabled="!editable"
                  class="mt-1 w-full rounded-lg border border-gray-300 bg-white p-2 text-slate-800 dark:border-gray-600 dark:bg-gray-700 dark:text-slate-100"
                  @input="setDayGroupDateDraft(activeEditorDay, $event.target.value)"
                >
              </div>
            </div>

            <div
              v-for="slotGroup in activeEditorDay.slotGroups"
              :key="slotGroup.renderKey"
              :ref="el => setEntryRef(slotGroup.matchKey, el)"
              class="editor-shift-card"
            >
              <div class="editor-shift-card__header">
                <div>
                  <div class="editor-shift-card__eyebrow">Shift</div>
                  <div class="editor-shift-card__meta">
                    <span class="editor-shift-chip">{{ getEntryTimeLabel(slotGroup) }}</span>
                    <span class="editor-shift-chip">{{ getDuration(slotGroup.startTime, slotGroup.finishTime) }}</span>
                    <span class="editor-shift-chip">{{ getSlotGroupResourceCount(slotGroup) }} resource{{ getSlotGroupResourceCount(slotGroup) === 1 ? '' : 's' }}</span>
                    <span
                      v-if="slotGroup.location_name || slotGroup.location_address"
                      class="editor-shift-chip"
                    >
                      {{ [slotGroup.location_name, slotGroup.location_address].filter(Boolean).join(' · ') }}
                    </span>
                  </div>
                </div>

                <div v-if="isScheduleMode" class="flex flex-wrap items-center gap-2">
                  <button class="rounded-full border border-sky-200 px-3 py-1 text-xs font-semibold text-sky-700 hover:bg-sky-50 dark:border-sky-700 dark:text-sky-300 dark:hover:bg-sky-900/40" @click.prevent="addResourceToSlotGroup(slotGroup)">
                    + Add Resource
                  </button>
                  <button class="rounded-full border border-rose-200 px-3 py-1 text-xs font-semibold text-rose-600 hover:bg-rose-50 dark:border-rose-800 dark:text-rose-300 dark:hover:bg-rose-900/40" @click.prevent="removeSlotGroup(slotGroup)">
                    Remove Shift
                  </button>
                </div>
              </div>

              <div v-if="isScheduleMode" class="editor-shift-card__times">
                <TimeField :editable="editable" :data="slotGroup.startTime" @update="val => updateSlotGroupStartTime(slotGroup, val)" />
                <span class="text-slate-400">to</span>
                <TimeField :editable="editable" :data="slotGroup.finishTime" @update="val => updateSlotGroupFinishTime(slotGroup, val)" />
              </div>

              <div v-if="isScheduleMode" class="grid gap-4 xl:grid-cols-[minmax(0,220px)_minmax(0,1fr)]">
                <div>
                  <label class="block text-xs font-semibold uppercase tracking-wide text-slate-400">Location Name</label>
                  <input
                    class="mt-1 w-full rounded-lg border border-gray-300 bg-white p-2 text-slate-800 dark:border-gray-600 dark:bg-gray-700 dark:text-slate-100"
                    :disabled="!editable"
                    :value="slotGroup.location_name ?? ''"
                    placeholder="Site area or zone"
                    @input="updateSlotGroupLocationName(slotGroup, $event.target.value)"
                  >
                </div>
                <div>
                  <label class="block text-xs font-semibold uppercase tracking-wide text-slate-400">Location Address</label>
                  <select
                    v-if="editable && hasAddresses"
                    class="mt-1 w-full rounded-lg border border-gray-300 bg-white p-2 text-slate-800 dark:border-gray-600 dark:bg-gray-700 dark:text-slate-100"
                    :value="slotGroup.location_address ?? ''"
                    @change="updateSlotGroupAddress(slotGroup, $event.target.value)"
                  >
                    <option value="">Select address</option>
                    <option v-if="isCustomAddress(slotGroup)" :value="slotGroup.location_address">
                      Current: {{ slotGroup.location_address }}
                    </option>
                    <option v-for="address in addressOptions" :key="address.id" :value="address.data">
                      {{ address.data }}
                    </option>
                  </select>
                  <input
                    v-else
                    class="mt-1 w-full rounded-lg border border-gray-300 bg-white p-2 text-slate-800 dark:border-gray-600 dark:bg-gray-700 dark:text-slate-100"
                    :disabled="!editable"
                    :value="slotGroup.location_address ?? ''"
                    placeholder="Venue address"
                    @input="updateSlotGroupAddress(slotGroup, $event.target.value)"
                  >
                </div>
              </div>

              <template v-if="isResourceMode">
              <div
                v-if="vehiclesLoadError"
                class="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950/30 dark:text-red-200"
                role="alert"
              >
                <span>{{ vehiclesLoadError }}</span>
                <button
                  type="button"
                  class="font-semibold underline disabled:opacity-60"
                  :disabled="vehiclesLoading"
                  data-testid="retry-load-vehicles"
                  @click.prevent="loadVehicles"
                >
                  {{ vehiclesLoading ? 'Retrying…' : 'Retry' }}
                </button>
              </div>

              <section
                v-for="allocationDate in getVehicleAllocationDatesForSlotGroup(slotGroup)"
                :key="`vehicle-allocation-date-${allocationDate.id ?? allocationDate.date_id}`"
                data-testid="vehicle-allocation-shift"
                class="overflow-hidden rounded-xl border border-sky-200 bg-sky-50/60 dark:border-sky-900 dark:bg-sky-950/20"
              >
                <header class="flex flex-wrap items-center justify-between gap-3 border-b border-sky-200 px-4 py-3 dark:border-sky-900">
                  <div>
                    <h3 class="text-xs font-semibold uppercase tracking-wide text-sky-800 dark:text-sky-200">
                      Vehicles &amp; drivers
                    </h3>
                    <p class="mt-1 text-xs text-slate-500 dark:text-slate-400">
                      Drivers are selected from the crew assigned to this shift.
                    </p>
                  </div>

                  <div class="flex flex-wrap items-center gap-2">
                    <span
                      class="rounded-full px-2 py-1 text-xs font-semibold"
                      :class="getVehicleAllocationsForSlotGroup(slotGroup).length
                        ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200'
                        : 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-200'"
                    >
                      {{ getVehicleAllocationsForSlotGroup(slotGroup).length }}
                      <template v-if="quotedVehicleMaximum !== null"> / {{ quotedVehicleMaximum }} quoted</template>
                      vehicle{{ quotedVehicleMaximum === 1 ? '' : 's' }}
                    </span>
                    <button
                      v-if="canCopyPreviousAssignments(slotGroup)"
                      type="button"
                      class="rounded-lg border border-sky-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-sky-700 hover:bg-sky-100 disabled:opacity-60 dark:border-sky-700 dark:bg-slate-800 dark:text-sky-300 dark:hover:bg-sky-950/40"
                      :disabled="isVehicleBusy(`copy-${allocationDate.id}`)"
                      title="Copies vehicles from the previous shift. Drivers are copied only when assigned to this shift."
                      @click.prevent="copyPreviousAssignments(slotGroup)"
                    >
                      {{ isVehicleBusy(`copy-${allocationDate.id}`) ? 'Copying…' : 'Copy previous shift' }}
                    </button>
                  </div>
                </header>

                <div class="space-y-3 p-4">
                  <div
                    v-if="getVehicleAllocationsForSlotGroup(slotGroup).length === 0"
                    class="rounded-lg border border-dashed border-slate-300 px-3 py-3 text-sm text-slate-500 dark:border-slate-600 dark:text-slate-400"
                  >
                    No vehicle has been assigned to this shift yet.
                  </div>

                  <div
                    v-for="allocation in getVehicleAllocationsForSlotGroup(slotGroup)"
                    :key="allocation.id"
                    class="grid gap-2 rounded-lg border border-slate-200 bg-white p-2 dark:border-slate-700 dark:bg-slate-800 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] xl:items-end"
                  >
                    <label class="block">
                      <span class="sr-only">Vehicle</span>
                      <span
                        v-if="isPersonalVehicleAllocation(allocation)"
                        class="mb-1 inline-flex rounded-full bg-violet-100 px-2 py-0.5 text-[11px] font-semibold text-violet-700 dark:bg-violet-900/40 dark:text-violet-200"
                      >
                        Own car
                      </span>
                      <input
                        v-if="isPersonalVehicleAllocation(allocation)"
                        :value="allocation.personal_vehicle_registration ?? ''"
                        data-testid="personal-vehicle-registration"
                        class="w-full rounded-lg border border-gray-300 bg-white p-2 text-sm uppercase text-slate-900 dark:border-gray-600 dark:bg-gray-700 dark:text-slate-100"
                        :disabled="isVehicleBusy(`allocation-${allocation.id}`)"
                        aria-label="Staff vehicle registration"
                        @change="onPersonalVehicleRegistrationChanged(allocation, $event.target.value)"
                      >
                      <select
                        v-else
                        :value="allocation.vehicle_id ?? ''"
                        class="w-full rounded-lg border border-gray-300 bg-white p-2 text-sm text-slate-900 dark:border-gray-600 dark:bg-gray-700 dark:text-slate-100"
                        :disabled="isVehicleBusy(`allocation-${allocation.id}`)"
                        @change="onAllocationVehicleChanged(allocation, $event.target.value)"
                      >
                        <option value="">Select vehicle</option>
                        <option
                          v-if="allocation.vehicle && !vehicles.find((vehicle) => Number(vehicle.id) === Number(allocation.vehicle.id))"
                          :value="allocation.vehicle.id"
                        >
                          {{ getVehicleLabel(allocation.vehicle) }}
                        </option>
                        <option
                          v-for="vehicle in vehicles"
                          :key="`vehicle-${allocation.id}-${vehicle.id}`"
                          :value="vehicle.id"
                          :disabled="isVehicleAllocatedElsewhere(slotGroup, allocation, vehicle.id)"
                        >
                          {{ getVehicleLabel(vehicle) }}
                        </option>
                      </select>
                    </label>

                    <label class="block">
                      <span class="sr-only">Driver</span>
                      <select
                        :value="allocation.driver_staff_id ?? ''"
                        class="w-full rounded-lg border border-gray-300 bg-white p-2 text-sm text-slate-900 dark:border-gray-600 dark:bg-gray-700 dark:text-slate-100"
                        :disabled="isVehicleBusy(`allocation-${allocation.id}`)"
                        @change="onAllocationDriverChanged(allocation, $event.target.value)"
                      >
                        <option value="" :disabled="isPersonalVehicleAllocation(allocation)">
                          {{ isPersonalVehicleAllocation(allocation) ? 'Select owner/driver' : 'No driver selected' }}
                        </option>
                        <option
                          v-for="driver in getDriverOptionsForAllocation(slotGroup, allocation)"
                          :key="`driver-${allocation.id}-${driver.id}`"
                          :value="driver.id"
                          :disabled="isDriverAllocatedElsewhere(slotGroup, allocation, driver.id)"
                        >
                          {{ driver.contact?.name ?? `Staff #${driver.id}` }}
                        </option>
                      </select>
                    </label>

                    <button
                      type="button"
                      class="self-end rounded-lg border border-red-300 px-3 py-2 text-xs font-semibold text-red-600 hover:border-red-400 hover:bg-red-50 disabled:opacity-60 dark:border-red-700 dark:text-red-300 dark:hover:bg-red-900/30"
                      :disabled="isVehicleBusy(`allocation-${allocation.id}`)"
                      title="Remove vehicle"
                      @click.prevent="removeVehicleAllocation(allocation)"
                    >
                      Remove
                    </button>
                  </div>

                  <div class="grid gap-2 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto]">
                    <select
                      :value="getPendingVehicleSelection(allocationDate)"
                      data-testid="add-vehicle-to-shift"
                      class="w-full rounded-lg border border-dashed border-sky-300 bg-white p-2 text-sm font-medium text-sky-800 dark:border-sky-700 dark:bg-slate-800 dark:text-sky-200"
                      :disabled="vehiclesLoading || Boolean(vehiclesLoadError) || isVehicleBusy(`create-${allocationDate.id}`) || getAddableVehicles(slotGroup).length === 0"
                      @change="onAddVehicleSelectionChanged(allocationDate, $event.target.value, slotGroup)"
                    >
                      <option value="">
                        {{ getAddVehiclePlaceholder(slotGroup) }}
                      </option>
                      <option
                        v-for="vehicle in getAddableVehicles(slotGroup)"
                        :key="`add-vehicle-${allocationDate.id}-${vehicle.id}`"
                        :value="vehicle.id"
                      >
                        {{ getVehicleLabel(vehicle) }}
                      </option>
                    </select>
                    <div class="flex items-center text-xs text-slate-500 dark:text-slate-400 xl:col-span-2">
                      Choose a vehicle, then select one of the assigned drivers.
                    </div>
                  </div>

                  <div
                    v-if="personalVehicleDraftFor(allocationDate)"
                    data-testid="personal-vehicle-draft"
                    class="grid gap-2 rounded-lg border border-dashed border-violet-300 bg-violet-50 p-3 dark:border-violet-800 dark:bg-violet-950/20 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto_auto]"
                  >
                    <input
                      :value="personalVehicleDraftFor(allocationDate).registration"
                      data-testid="new-personal-vehicle-registration"
                      class="w-full rounded-lg border border-gray-300 bg-white p-2 text-sm uppercase text-slate-900 dark:border-gray-600 dark:bg-gray-700 dark:text-slate-100"
                      placeholder="Registration"
                      aria-label="Staff vehicle registration"
                      @input="updatePersonalVehicleDraft(allocationDate, 'registration', $event.target.value)"
                    >
                    <select
                      :value="personalVehicleDraftFor(allocationDate).driver_staff_id"
                      data-testid="new-personal-vehicle-driver"
                      class="w-full rounded-lg border border-gray-300 bg-white p-2 text-sm text-slate-900 dark:border-gray-600 dark:bg-gray-700 dark:text-slate-100"
                      aria-label="Staff vehicle driver"
                      @change="updatePersonalVehicleDraft(allocationDate, 'driver_staff_id', $event.target.value)"
                    >
                      <option value="">Select owner/driver</option>
                      <option
                        v-for="driver in getAssignedDriverOptions(slotGroup)"
                        :key="`personal-driver-${allocationDate.id}-${driver.id}`"
                        :value="driver.id"
                        :disabled="isDriverAllocatedElsewhere(slotGroup, null, driver.id)"
                      >
                        {{ driver.contact?.name ?? `Staff #${driver.id}` }}
                      </option>
                    </select>
                    <button
                      type="button"
                      class="rounded-lg bg-violet-700 px-3 py-2 text-xs font-semibold text-white disabled:opacity-50"
                      :disabled="!personalVehicleDraftFor(allocationDate).registration || !personalVehicleDraftFor(allocationDate).driver_staff_id || isVehicleBusy(`create-personal-${allocationDate.id}`)"
                      @click.prevent="addPersonalVehicle(allocationDate, slotGroup)"
                    >
                      {{ isVehicleBusy(`create-personal-${allocationDate.id}`) ? 'Adding…' : 'Add own car' }}
                    </button>
                    <button
                      type="button"
                      class="rounded-lg border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-600 dark:border-slate-600 dark:text-slate-300"
                      :disabled="isVehicleBusy(`create-personal-${allocationDate.id}`)"
                      @click.prevent="cancelPersonalVehicle(allocationDate)"
                    >
                      Cancel
                    </button>
                  </div>
                  <button
                    v-else
                    type="button"
                    data-testid="add-personal-vehicle"
                    class="rounded-lg border border-dashed border-violet-300 bg-white px-3 py-2 text-sm font-semibold text-violet-700 dark:border-violet-700 dark:bg-slate-800 dark:text-violet-300"
                    @click.prevent="startPersonalVehicle(allocationDate, slotGroup)"
                  >
                    + Add staff's own car
                  </button>

                  <p
                    v-if="getVehicleLimitMessage(slotGroup)"
                    data-testid="vehicle-limit-message"
                    class="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200"
                  >
                    {{ getVehicleLimitMessage(slotGroup) }}
                  </p>

                  <p
                    v-if="getVehicleActionError(slotGroup)"
                    class="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-950/30 dark:text-red-200"
                    role="alert"
                  >
                    {{ getVehicleActionError(slotGroup) }}
                  </p>

                  <p
                    v-if="getAssignedDriverOptions(slotGroup).length === 0"
                    class="text-xs text-amber-700 dark:text-amber-300"
                  >
                    Assign a crew member with a driver skill to this shift to make them available here.
                  </p>
                </div>
              </section>

              <div
                v-if="getSavedEntriesForSlotGroup(slotGroup).length === 0"
                class="rounded-xl border border-dashed border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-950/30 dark:text-amber-200"
              >
                Save this shift before assigning its vehicles and drivers.
              </div>
              </template>

              <div
                v-if="isScheduleMode"
                class="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600 dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-300"
              >
                <div class="mb-3 font-semibold">Units and staff slots</div>
                <div v-for="group in getEditorResourceGroups(slotGroup)" :key="group.key" class="mb-3 grid gap-3 sm:grid-cols-[1fr_8rem_1fr]">
                  <label>Unit type
                    <select :value="group.productId" class="mt-1 w-full" @change="updateScheduleUnitType(group, $event.target.value); $event.target.value = group.resources[0].resource.product_id">
                      <option v-for="product in products" :key="product.id" :value="product.id">{{ product.name }}</option>
                    </select>
                  </label>
                  <label>Quantity
                    <input type="number" min="0" step="1" :value="scheduleUnitQuantity(group)" class="mt-1 w-full" @change="updateScheduleUnitQuantity(slotGroup, group, $event.target.value); $event.target.value = scheduleUnitQuantity(getEditorResourceGroups(slotGroup).find(next => next.productId === group.productId) ?? { resources: [] })">
                  </label>
                  <div class="self-center">{{ group.required }} staff slots required</div>
                </div>
                <p class="text-xs">Save this setup, then assign people and vehicles in Resources. Reducing quantities removes empty slots only. Unassign crew before changing occupied unit types.</p>
              </div>

              <div v-else-if="getEditorResourceGroups(slotGroup).length" class="resource-product-groups">
                <section
                  v-for="resourceGroup in getEditorResourceGroups(slotGroup)"
                  :key="resourceGroup.key"
                  class="resource-product-group"
                >
                  <button
                    type="button"
                    class="resource-product-group__toggle"
                    :aria-expanded="isResourceGroupExpanded(resourceGroup)"
                    @click.prevent="toggleResourceGroup(resourceGroup)"
                  >
                    <div>
                      <div class="resource-product-group__title">
                        {{ resourceGroup.productName }} ×{{ resourceGroup.resources.length }}
                      </div>
                      <div
                        class="resource-product-group__coverage"
                        :class="resourceGroup.filled >= resourceGroup.required ? 'text-emerald-600 dark:text-emerald-300' : 'text-amber-600 dark:text-amber-300'"
                      >
                        {{ resourceGroup.filled }} / {{ resourceGroup.required }} crew assigned
                      </div>
                    </div>
                    <ChevronDownIcon
                      class="editor-day-group__chevron"
                      :class="{ 'editor-day-group__chevron--open': isResourceGroupExpanded(resourceGroup) }"
                    />
                  </button>

                  <div v-if="isResourceGroupExpanded(resourceGroup)" class="resource-card-grid grid gap-4">
                    <div
                      v-for="item in resourceGroup.resources"
                      :key="getResourceRenderKey(slotGroup.entries[0], item.resource, item.originalIndex)"
                      :ref="el => setResourceRef(getResourceRenderKey(slotGroup.entries[0], item.resource, item.originalIndex), el)"
                      class="resource-editor-card"
                    >
                      <div class="resource-editor-card__header">
                        <div>
                          <span class="resource-editor-card__eyebrow">Unit {{ item.originalIndex + 1 }}</span>
                          <div class="resource-editor-card__title">{{ getProductName(item.resource.product_id) }}</div>
                          <div class="resource-editor-card__meta" :class="getResourceCoverageTone(item.resource)">
                            {{ getResourceCoverageLabel(item.resource) }}
                          </div>
                        </div>

                        <button v-if="isScheduleMode" class="text-xs font-semibold text-rose-600 hover:underline" @click.prevent="removeResource(item.resource)">
                          Remove
                        </button>
                      </div>

                      <div class="grid gap-4" :class="{ 'xl:grid-cols-[minmax(0,220px)_minmax(0,1fr)]': isResourceMode }">
                        <div v-if="isScheduleMode">
                          <label class="block text-xs font-semibold uppercase tracking-wide text-slate-400">Unit Type</label>
                          <select
                            v-model="item.resource.product_id"
                            class="mt-1 w-full rounded-lg border border-gray-300 bg-white p-2 text-slate-800 dark:border-gray-600 dark:bg-gray-700 dark:text-slate-100"
                            @change="changeProduct(item.resource, $event.target.value)"
                          >
                            <option v-for="product in products" :key="product.id" :value="product.id">{{ product.name }}</option>
                          </select>
                        </div>

                        <div v-if="isResourceMode" class="space-y-3">
                          <div class="flex items-center justify-between gap-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
                            <span>Crew allocation</span>
                            <span
                              class="normal-case tracking-normal"
                              :class="item.resource.id ? 'text-emerald-600 dark:text-emerald-300' : 'text-amber-600 dark:text-amber-300'"
                            >
                              {{ item.resource.id ? 'Saved instantly' : 'Saved with schedule' }}
                            </span>
                          </div>
                          <div v-for="(personnel, idx) in getPersonnelSlots(item.resource)" :key="personnel.id ?? idx">
                            <template v-if="personnel.staff">
                              <div class="flex items-center gap-2">
                                <input :value="personnel.staff.contact.name" readonly class="w-full cursor-default rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-slate-800 dark:border-slate-700 dark:bg-slate-700 dark:text-slate-100" />
                                <button type="button" class="rounded-full p-1 text-slate-500 hover:text-slate-700 dark:text-slate-300 dark:hover:text-white" @click.prevent="unassignStaff(item.resource, personnel)">
                                  <XMarkIcon class="h-4 w-4" />
                                </button>
                              </div>
                            </template>

                            <SearchableInput
                              v-else
                              class="flex-1"
                              :url="`/api/staff/search?skill_id=${personnel.staff_skill_id}&query=`"
                              :placeholder="`Search for ${personnel.skill.name}`"
                              :output="val => val.contact.name + ' [' + val.contact.email + ']'"
                              @selected="staff => addStaffResource(item.resource, personnel, staff)"
                            />
                            <p v-if="isStaffBusy(item.resource, personnel)" class="mt-1 text-xs text-slate-500 dark:text-slate-400">
                              Saving crew assignment…
                            </p>
                          </div>
                          <p
                            v-if="getStaffActionError(item.resource)"
                            class="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-800 dark:bg-red-950/30 dark:text-red-200"
                            role="alert"
                          >
                            {{ getStaffActionError(item.resource) }}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>
              </div>

              <div v-else class="planner-empty-state">
                {{ isResourceMode && slotGroup.resources?.length ? 'No resources match the current filters.' : 'No resources in this shift yet.' }}
              </div>
            </div>
          </div>
        </section>

        <div
          v-if="isResourceMode && getVehicleAllocations().some(allocation => !allocation.job_date_id)"
          class="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200"
        >
          This job has an older job-wide assignment. Save the job after running the latest database migration to attach it to each shift.
        </div>
      </div>
    </div>
    <slot name="footer" />
  </FormBox>
</template>

<style scoped>
.planner-shell {
  --planner-date-column-width: 11.25rem;
  --planner-hour-width: 3rem;
  --planner-hour-count: 24;
  --planner-timeline-width: calc(var(--planner-hour-width) * var(--planner-hour-count));
  overflow-x: auto;
  overflow-y: hidden;
  border: 1px solid rgb(226 232 240);
  border-radius: 1rem;
  background:
    linear-gradient(180deg, rgba(248, 250, 252, 0.96) 0%, rgba(255, 255, 255, 0.98) 100%);
  box-shadow: 0 18px 45px -28px rgba(15, 23, 42, 0.35);
}

.planner-shell--desktop {
  display: block;
}

.planner-header-row {
  display: grid;
  grid-template-columns: var(--planner-date-column-width) minmax(var(--planner-timeline-width), 1fr);
  width: 100%;
  min-width: calc(var(--planner-date-column-width) + var(--planner-timeline-width));
  border-bottom: 1px solid rgb(226 232 240);
  background: linear-gradient(180deg, rgba(241, 245, 249, 0.96) 0%, rgba(248, 250, 252, 0.92) 100%);
}

.planner-date-header,
.planner-date-cell {
  padding: 1rem;
  border-right: 1px solid rgb(226 232 240);
}

.planner-date-header {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgb(100 116 139);
}

.planner-hours-header {
  display: grid;
  grid-template-columns: repeat(var(--planner-hour-count), minmax(var(--planner-hour-width), 1fr));
  width: 100%;
  min-width: var(--planner-timeline-width);
}

.planner-hour-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 60px;
  border-left: 1px solid rgb(226 232 240);
  font-size: 0.72rem;
  font-weight: 700;
  color: rgb(100 116 139);
}

.planner-entry-row {
  display: grid;
  grid-template-columns: var(--planner-date-column-width) minmax(var(--planner-timeline-width), 1fr);
  width: 100%;
  min-width: calc(var(--planner-date-column-width) + var(--planner-timeline-width));
  border-bottom: 1px solid rgb(226 232 240);
}

.planner-entry-row:last-child {
  border-bottom: 0;
}

.planner-date-cell {
  background: rgba(248, 250, 252, 0.88);
}

.planner-date-title {
  font-size: 0.95rem;
  font-weight: 700;
  color: rgb(15 23 42);
}

.planner-date-subtitle,
.planner-date-meta {
  margin-top: 0.2rem;
  font-size: 0.76rem;
  color: rgb(100 116 139);
}

.planner-timeline-cell {
  position: relative;
  width: 100%;
  min-width: var(--planner-timeline-width);
  padding: 0.8rem 0;
}

.planner-grid-backdrop {
  position: absolute;
  inset: 0;
  background-image: repeating-linear-gradient(
    to right,
    transparent,
    transparent calc((100% / var(--planner-hour-count)) - 1px),
    rgba(226, 232, 240, 0.9) calc((100% / var(--planner-hour-count)) - 1px),
    rgba(226, 232, 240, 0.9) calc(100% / var(--planner-hour-count))
  );
  pointer-events: none;
}

.planner-track {
  position: relative;
  height: 2.4rem;
  margin: 0.35rem 0;
}

.planner-bar {
  position: absolute;
  top: 0;
  bottom: 0;
  min-width: 5rem;
  border-radius: 999px;
  padding: 0.35rem 0.8rem;
  overflow: hidden;
  box-shadow: 0 10px 18px -14px rgba(15, 23, 42, 0.7);
  display: flex;
  flex-direction: column;
  justify-content: center;
  color: white;
}

.planner-bar-title,
.planner-bar-meta {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.planner-bar-title {
  font-size: 0.74rem;
  font-weight: 700;
}

.planner-bar-meta {
  font-size: 0.64rem;
  opacity: 0.92;
}

.planner-bar--emerald {
  background: linear-gradient(90deg, #22c55e 0%, #16a34a 100%);
}

.planner-bar--rose {
  background: linear-gradient(90deg, #f43f5e 0%, #e11d48 100%);
}

.planner-bar--violet {
  background: linear-gradient(90deg, #8b5cf6 0%, #7c3aed 100%);
}

.planner-bar--cyan {
  background: linear-gradient(90deg, #06b6d4 0%, #0891b2 100%);
}

.planner-bar--amber {
  background: linear-gradient(90deg, #f59e0b 0%, #d97706 100%);
}

.planner-bar--fuchsia {
  background: linear-gradient(90deg, #d946ef 0%, #c026d3 100%);
}

.planner-empty-state,
.planner-empty-board {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 4.5rem;
  padding: 1rem;
  font-size: 0.85rem;
  color: rgb(100 116 139);
}

.planner-empty-board {
  border-top: 1px solid rgb(226 232 240);
}

.planner-mobile-list {
  display: none;
}

.planner-mobile-card,
.planner-mobile-empty {
  border: 1px solid rgb(226 232 240);
  border-radius: 1rem;
  background: linear-gradient(180deg, rgba(248, 250, 252, 0.92) 0%, rgba(255, 255, 255, 0.98) 100%);
  box-shadow: 0 18px 40px -32px rgba(15, 23, 42, 0.45);
  padding: 1rem;
}

.planner-mobile-card__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
}

.planner-mobile-card__title {
  font-size: 0.98rem;
  font-weight: 700;
  color: rgb(15 23 42);
}

.planner-mobile-card__subtitle,
.planner-mobile-card__location {
  margin-top: 0.2rem;
  font-size: 0.8rem;
  color: rgb(100 116 139);
}

.planner-mobile-card__summary {
  flex-shrink: 0;
  padding: 0.35rem 0.65rem;
  border-radius: 999px;
  background: rgba(226, 232, 240, 0.9);
  font-size: 0.72rem;
  font-weight: 700;
  color: rgb(51 65 85);
}

.planner-mobile-card__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.8rem;
}

.planner-mobile-chip {
  display: inline-flex;
  align-items: center;
  padding: 0.34rem 0.65rem;
  border-radius: 999px;
  background: rgba(224, 242, 254, 0.95);
  color: rgb(7 89 133);
  font-size: 0.74rem;
  font-weight: 700;
}

.planner-mobile-axis {
  position: relative;
  height: 1rem;
  margin-top: 0.9rem;
  font-size: 0.68rem;
  font-weight: 700;
  color: rgb(100 116 139);
}

.planner-mobile-axis span {
  position: absolute;
  top: 0;
  white-space: nowrap;
}

.planner-mobile-resource-list {
  display: grid;
  gap: 0.85rem;
  margin-top: 0.85rem;
}

.planner-mobile-resource {
  display: grid;
  gap: 0.45rem;
}

.planner-mobile-resource__labels {
  display: grid;
  gap: 0.12rem;
}

.planner-mobile-resource__title {
  font-size: 0.8rem;
  font-weight: 700;
  color: rgb(15 23 42);
}

.planner-mobile-resource__meta {
  font-size: 0.72rem;
  color: rgb(100 116 139);
}

.planner-mobile-track {
  position: relative;
  height: 2.6rem;
  overflow: hidden;
  border-radius: 1rem;
  background: rgba(241, 245, 249, 0.9);
}

.planner-mobile-grid-backdrop {
  position: absolute;
  inset: 0;
  background-image: repeating-linear-gradient(
    to right,
    transparent,
    transparent calc((100% / var(--planner-hour-count)) - 1px),
    rgba(203, 213, 225, 0.8) calc((100% / var(--planner-hour-count)) - 1px),
    rgba(203, 213, 225, 0.8) calc(100% / var(--planner-hour-count))
  );
  pointer-events: none;
}

.planner-bar--mobile {
  top: 0.35rem;
  bottom: 0.35rem;
  min-width: 3.75rem;
  padding: 0.3rem 0.65rem;
}

.planner-empty-state--mobile {
  min-height: 0;
  justify-content: flex-start;
  padding: 0.5rem 0 0;
}

.planner-mobile-empty {
  font-size: 0.85rem;
  color: rgb(100 116 139);
}

@media (prefers-color-scheme: dark) {
  .planner-shell {
    border-color: rgb(51 65 85);
    background: rgb(30 41 59);
    box-shadow: 0 18px 45px -28px rgba(2, 6, 23, 0.9);
  }

  .planner-header-row {
    border-bottom-color: rgb(51 65 85);
    background: rgb(30 41 59);
  }

  .planner-date-header,
  .planner-date-cell {
    border-right-color: rgb(51 65 85);
  }

  .planner-date-header,
  .planner-hour-cell,
  .planner-date-subtitle,
  .planner-date-meta,
  .planner-empty-state,
  .planner-empty-board,
  .planner-mobile-card__subtitle,
  .planner-mobile-card__location,
  .planner-mobile-axis,
  .planner-mobile-resource__meta,
  .planner-mobile-empty {
    color: rgb(203 213 225);
  }

  .planner-hour-cell {
    border-left-color: rgb(51 65 85);
  }

  .planner-entry-row {
    border-bottom-color: rgb(51 65 85);
  }

  .planner-date-cell {
    background: rgb(30 41 59);
  }

  .planner-date-title,
  .planner-mobile-card__title,
  .planner-mobile-resource__title {
    color: rgb(241 245 249);
  }

  .planner-grid-backdrop {
    background-image: repeating-linear-gradient(
      to right,
      transparent,
      transparent calc((100% / var(--planner-hour-count)) - 1px),
      rgba(71, 85, 105, 0.7) calc((100% / var(--planner-hour-count)) - 1px),
      rgba(71, 85, 105, 0.7) calc(100% / var(--planner-hour-count))
    );
  }

  .planner-empty-board {
    border-top-color: rgb(51 65 85);
  }

  .planner-mobile-card,
  .planner-mobile-empty {
    border-color: rgb(51 65 85);
    background: rgb(30 41 59);
    box-shadow: 0 18px 40px -32px rgba(2, 6, 23, 0.95);
  }

  .planner-mobile-card__summary {
    background: rgba(51, 65, 85, 0.9);
    color: rgb(226 232 240);
  }

  .planner-mobile-chip {
    background: rgba(8, 145, 178, 0.22);
    color: rgb(165 243 252);
  }

  .planner-mobile-track {
    background: rgb(30 41 59);
  }

  .planner-mobile-grid-backdrop {
    background-image: repeating-linear-gradient(
      to right,
      transparent,
      transparent calc((100% / var(--planner-hour-count)) - 1px),
      rgba(71, 85, 105, 0.72) calc((100% / var(--planner-hour-count)) - 1px),
      rgba(71, 85, 105, 0.72) calc(100% / var(--planner-hour-count))
    );
  }
}

.resource-workspace {
  display: grid;
  gap: 1rem;
  align-items: start;
}

.editor-day-nav {
  display: grid;
  gap: 0.75rem;
  padding: 1rem;
  border: 1px solid rgb(226 232 240);
  border-radius: 1rem;
  background: rgba(255, 255, 255, 0.78);
  box-shadow: 0 14px 34px -30px rgba(15, 23, 42, 0.6);
}

.editor-day-nav__label {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: rgb(100 116 139);
}

.editor-day-nav__chips {
  display: grid;
  gap: 0.65rem;
}

.editor-day-chip {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.1rem;
  padding: 0.7rem 0.9rem;
  border: 1px solid rgb(186 230 253);
  border-radius: 0.9rem;
  background: linear-gradient(180deg, rgba(240, 249, 255, 0.96) 0%, rgba(224, 242, 254, 0.96) 100%);
  color: rgb(12 74 110);
  font-size: 0.8rem;
  font-weight: 600;
  text-align: left;
  transition: transform 120ms ease, box-shadow 120ms ease;
}

.editor-day-chip--active {
  border-color: rgb(14 165 233);
  background: linear-gradient(180deg, rgba(224, 242, 254, 1) 0%, rgba(186, 230, 253, 0.98) 100%);
  box-shadow: 0 12px 28px -20px rgba(3, 105, 161, 0.9);
}

.editor-day-chip:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 22px -18px rgba(3, 105, 161, 0.9);
}

.editor-day-group {
  border: 1px solid rgb(226 232 240);
  border-radius: 1rem;
  background: rgba(255, 255, 255, 0.82);
  box-shadow: 0 18px 40px -32px rgba(15, 23, 42, 0.5);
}

.editor-day-group__toggle {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.1rem;
  text-align: left;
}

.editor-day-group__summary {
  min-width: 0;
}

.editor-day-group__eyebrow {
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: rgb(100 116 139);
}

.editor-day-group__title {
  margin-top: 0.2rem;
  font-size: 1rem;
  font-weight: 700;
  color: rgb(15 23 42);
}

.editor-day-group__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 0.45rem;
  font-size: 0.8rem;
  color: rgb(100 116 139);
}

.editor-day-group__chevron {
  width: 1.2rem;
  height: 1.2rem;
  color: rgb(100 116 139);
  transition: transform 160ms ease;
  flex-shrink: 0;
}

.editor-day-group__chevron--open {
  transform: rotate(180deg);
}

.resource-editor-filters {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.85rem;
  margin: 0 1rem;
  padding: 0.85rem;
  border: 1px solid rgb(226 232 240);
  border-radius: 0.8rem;
  background: rgba(248, 250, 252, 0.86);
}

.editor-shift-card {
  margin: 0 1rem 1rem;
  padding: 1rem;
  border: 1px solid rgb(226 232 240);
  border-radius: 0.95rem;
  background: linear-gradient(180deg, rgba(248, 250, 252, 0.9) 0%, rgba(255, 255, 255, 0.96) 100%);
}

.editor-shift-card__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

.editor-shift-card__eyebrow {
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: rgb(100 116 139);
}

.editor-shift-card__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.55rem;
}

.editor-shift-card__times {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-top: 0.9rem;
  margin-bottom: 1rem;
  font-size: 0.85rem;
}

.editor-shift-chip {
  display: inline-flex;
  align-items: center;
  padding: 0.34rem 0.65rem;
  border-radius: 999px;
  background: rgba(226, 232, 240, 0.8);
  color: rgb(51 65 85);
  font-size: 0.76rem;
  font-weight: 700;
}

.resource-editor-card {
  border: 1px solid rgb(226 232 240);
  border-radius: 0.9rem;
  background: rgba(248, 250, 252, 0.75);
  padding: 1rem;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.7);
}

.resource-card-grid {
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 32rem), 1fr));
  padding: 0 1rem 1rem;
}

.resource-product-groups {
  display: grid;
  gap: 0.75rem;
}

.resource-product-group {
  overflow: hidden;
  border: 1px solid rgb(203 213 225);
  border-radius: 0.9rem;
  background: rgba(255, 255, 255, 0.78);
}

.resource-product-group__toggle {
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.9rem 1rem;
  text-align: left;
}

.resource-product-group__title {
  font-size: 0.9rem;
  font-weight: 700;
  color: rgb(15 23 42);
}

.resource-product-group__coverage {
  margin-top: 0.15rem;
  font-size: 0.76rem;
  font-weight: 600;
}

.resource-editor-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.85rem;
}

.resource-editor-card__eyebrow {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgb(100 116 139);
}

.resource-editor-card__title {
  margin-top: 0.2rem;
  font-size: 0.92rem;
  font-weight: 700;
  color: rgb(15 23 42);
}

.resource-editor-card__meta {
  margin-top: 0.2rem;
  font-size: 0.76rem;
  font-weight: 600;
}

.flash-added-entry {
  animation: flashEntry 1.6s ease;
}

.flash-added-resource {
  animation: flashResource 1.6s ease;
}

@keyframes flashEntry {
  0% {
    background: rgba(16, 185, 129, 0.2);
    box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.35);
  }
  70% {
    background: rgba(16, 185, 129, 0.1);
    box-shadow: 0 0 0 8px rgba(16, 185, 129, 0);
  }
  100% {
    background: transparent;
    box-shadow: 0 0 0 0 rgba(16, 185, 129, 0);
  }
}

@keyframes flashResource {
  0% {
    background: rgba(14, 165, 233, 0.2);
    box-shadow: 0 0 0 0 rgba(14, 165, 233, 0.35);
  }
  70% {
    background: rgba(14, 165, 233, 0.08);
    box-shadow: 0 0 0 6px rgba(14, 165, 233, 0);
  }
  100% {
    background: transparent;
    box-shadow: 0 0 0 0 rgba(14, 165, 233, 0);
  }
}

@media (prefers-color-scheme: dark) {
  .editor-day-nav,
  .editor-day-group {
    border-color: rgb(51 65 85);
    background: rgb(30 41 59);
    box-shadow: 0 18px 40px -32px rgba(2, 6, 23, 0.95);
  }

  .resource-editor-filters,
  .resource-product-group {
    border-color: rgb(51 65 85);
    background: rgb(30 41 59);
  }

  .resource-product-group__title {
    color: rgb(241 245 249);
  }

  .editor-day-nav__label,
  .editor-day-group__eyebrow,
  .editor-day-group__meta,
  .editor-day-group__chevron,
  .editor-shift-card__eyebrow,
  .resource-editor-card__eyebrow {
    color: rgb(148 163 184);
  }

  .editor-day-chip {
    border-color: rgb(14 116 144);
    background: linear-gradient(180deg, rgba(8, 47, 73, 0.9) 0%, rgba(12, 74, 110, 0.88) 100%);
    color: rgb(186 230 253);
  }

  .editor-day-chip--active {
    border-color: rgb(56 189 248);
    background: linear-gradient(180deg, rgba(3, 105, 161, 0.96) 0%, rgba(14, 116, 144, 0.94) 100%);
  }

  .editor-day-group__title,
  .resource-editor-card__title {
    color: rgb(241 245 249);
  }

  .editor-shift-card {
    border-color: rgb(51 65 85);
    background: rgb(30 41 59);
  }

  .editor-shift-chip {
    background: rgba(51, 65, 85, 0.9);
    color: rgb(226 232 240);
  }

  .resource-editor-card {
    border-color: rgb(71 85 105);
    background: rgba(30, 41, 59, 0.78);
    box-shadow: inset 0 1px 0 rgba(148, 163, 184, 0.08);
  }
}

@media (min-width: 1280px) {
  .resource-workspace {
    grid-template-columns: minmax(13.5rem, 17rem) minmax(0, 1fr);
  }

  .editor-day-nav {
    position: sticky;
    top: 5.5rem;
    max-height: calc(100vh - 7rem);
    overflow-y: auto;
  }
}

@media (max-width: 1535px) {
  .planner-shell--desktop {
    display: none;
  }

  .planner-mobile-list {
    display: grid;
    gap: 0.9rem;
  }

  .editor-shift-card__header,
  .editor-day-group__toggle,
  .editor-shift-card__times {
    flex-direction: column;
    align-items: flex-start;
  }
}

</style>
